// ================================================================
// src/components/Lesson-planning/Lesson-planning.jsx
// نسخه نهایی با پیاده‌سازی کامل تمام قوانین و شرایط + پشتیبانی از شیفت عصر
// ================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCalendar, FiClock, FiBookOpen, FiCheckCircle, FiCircle,
  FiTrash2, FiBookmark, FiPlus, FiMenu, FiX,
  FiStar, FiPlay, FiPause, FiRefreshCw,
  FiGrid, FiList, FiEdit, FiFileText, FiAlertCircle,
  FiChevronDown, FiChevronUp, FiInfo, FiPrinter,
  FiChevronLeft, FiChevronRight, FiSave, FiHome,
  FiChevronDown as FiDown, FiChevronUp as FiUp, FiPlusCircle
} from 'react-icons/fi';
import { FaRobot, FaBrain, FaSchool, FaChartLine } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useCoins } from '../../context/CoinContext';
import './Lesson-planning.css';
import moment from 'moment-jalaali';
import {
  BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { useNavigate } from 'react-router-dom';

// ============================================================
// پیکربندی API
// ============================================================
const CONFIG = {
  modelText: 'gpt-4o-mini',
  baseURL: '/api/ai/chat', // ✅ آدرس پروکسی سرور خودمان
  requestTimeoutMs: 45000,
};

// ============================================================
// دیکشنری کتاب‌های درسی
// ============================================================
const SUBJECTS_DB = {
  // ===== دوره ابتدایی =====
  'پایه اول': {
    books: ['آموزش قرآن', 'فارسی', 'ریاضی', 'علوم تجربی'],
    sessionsPerDay: 3, sessionDuration: 20, breakDuration: 10, weeklyHours: 2, gradeType: 'elementary'
  },
  'پایه دوم': {
    books: ['آموزش قرآن', 'هدیه‌های آسمان', 'فارسی', 'ریاضی', 'علوم تجربی'],
    sessionsPerDay: 3, sessionDuration: 25, breakDuration: 10, weeklyHours: 2.5, gradeType: 'elementary'
  },
  'پایه سوم': {
    books: ['آموزش قرآن', 'هدیه‌های آسمان', 'فارسی', 'ریاضی', 'علوم تجربی', 'مطالعات اجتماعی'],
    sessionsPerDay: 4, sessionDuration: 30, breakDuration: 10, weeklyHours: 3, gradeType: 'elementary'
  },
  'پایه چهارم': {
    books: ['آموزش قرآن', 'هدیه‌های آسمان', 'فارسی', 'ریاضی', 'علوم تجربی', 'مطالعات اجتماعی'],
    sessionsPerDay: 4, sessionDuration: 30, breakDuration: 10, weeklyHours: 3.5, gradeType: 'elementary'
  },
  'پایه پنجم': {
    books: ['آموزش قرآن', 'هدیه‌های آسمان', 'فارسی', 'ریاضی', 'علوم تجربی', 'مطالعات اجتماعی'],
    sessionsPerDay: 4, sessionDuration: 35, breakDuration: 10, weeklyHours: 4, gradeType: 'elementary'
  },
  'پایه ششم': {
    books: ['آموزش قرآن', 'هدیه‌های آسمان', 'فارسی', 'ریاضی', 'علوم تجربی', 'مطالعات اجتماعی'],
    sessionsPerDay: 5, sessionDuration: 35, breakDuration: 10, weeklyHours: 4.5, gradeType: 'elementary'
  },

  // ===== دوره متوسطه اول =====
  'پایه هفتم': {
    books: ['آموزش قرآن', 'پیام‌های آسمان', 'فارسی', 'ریاضی', 'علوم تجربی', 'مطالعات اجتماعی', 'فرهنگ و هنر', 'عربی، زبان قرآن', 'زبان انگلیسی'],
    sessionsPerDay: 5, sessionDuration: 45, breakDuration: 10, weeklyHours: 5.5, gradeType: 'middle'
  },
  'پایه هشتم': {
    books: ['آموزش قرآن', 'پیام‌های آسمان', 'فارسی', 'ریاضی', 'علوم تجربی', 'مطالعات اجتماعی', 'فرهنگ و هنر', 'عربی، زبان قرآن', 'زبان انگلیسی'],
    sessionsPerDay: 5, sessionDuration: 45, breakDuration: 10, weeklyHours: 6, gradeType: 'middle'
  },
  'پایه نهم': {
    books: ['آموزش قرآن', 'پیام‌های آسمان', 'فارسی', 'ریاضی', 'علوم تجربی', 'مطالعات اجتماعی', 'فرهنگ و هنر', 'عربی، زبان قرآن', 'زبان انگلیسی'],
    sessionsPerDay: 5, sessionDuration: 50, breakDuration: 10, weeklyHours: 6.5, gradeType: 'middle'
  },

  // ===== دهم ریاضی و فیزیک =====
  'پایه دهم ریاضی': {
    books: [
      'فارسی (۱)',
      'دین و زندگی (۱)',
      'عربی، زبان قرآن (۱)',
      'زبان انگلیسی (۱)',
      'ریاضی (۱)',
      'هندسه (۱)',
      'فیزیک (۱)',
      'شیمی (۱)',
      'جغرافیای ایران',
    ],
    sessionsPerDay: 6, sessionDuration: 60, breakDuration: 12, weeklyHours: 8, gradeType: 'high', field: 'ریاضی'
  },
  'پایه یازدهم ریاضی': {
    books: [
      'فارسی (۲)',
      'دین و زندگی (۲)',
      'عربی، زبان قرآن (۲)',
      'زبان انگلیسی (۲)',
      'حسابان (۱)',
      'هندسه (۲)',
      'آمار و احتمال',
      'فیزیک (۲)',
      'شیمی (۲)',
      'تاریخ معاصر ایران',
    ],
    sessionsPerDay: 6, sessionDuration: 65, breakDuration: 12, weeklyHours: 9, gradeType: 'high', field: 'ریاضی'
  },
  'پایه دوازدهم ریاضی': {
    books: [
      'فارسی (۳)',
      'دین و زندگی (۳)',
      'عربی، زبان قرآن (۳)',
      'زبان انگلیسی (۳)',
      'حسابان (۲)',
      'هندسه (۳)',
      'ریاضیات گسسته',
      'فیزیک (۳)',
      'شیمی (۳)',
      'هویت اجتماعی',
      'سلامت و بهداشت',
    ],
    sessionsPerDay: 6, sessionDuration: 70, breakDuration: 15, weeklyHours: 10, gradeType: 'high', field: 'ریاضی'
  },

  // ===== دهم علوم تجربی =====
  'پایه دهم تجربی': {
    books: [
      'فارسی (۱)',
      'دین و زندگی (۱)',
      'عربی، زبان قرآن (۱)',
      'زبان انگلیسی (۱)',
      'ریاضی (۱)',
      'زیست‌شناسی (۱)',
      'فیزیک (۱)',
      'شیمی (۱)',
      'جغرافیای ایران',
    ],
    sessionsPerDay: 6, sessionDuration: 60, breakDuration: 12, weeklyHours: 8, gradeType: 'high', field: 'تجربی'
  },
  'پایه یازدهم تجربی': {
    books: [
      'فارسی (۲)',
      'دین و زندگی (۲)',
      'عربی، زبان قرآن (۲)',
      'زبان انگلیسی (۲)',
      'ریاضی (۲)',
      'زیست‌شناسی (۲)',
      'فیزیک (۲)',
      'شیمی (۲)',
      'تاریخ معاصر ایران',
    ],
    sessionsPerDay: 6, sessionDuration: 65, breakDuration: 12, weeklyHours: 9, gradeType: 'high', field: 'تجربی'
  },
  'پایه دوازدهم تجربی': {
    books: [
      'فارسی (۳)',
      'دین و زندگی (۳)',
      'عربی، زبان قرآن (۳)',
      'زبان انگلیسی (۳)',
      'ریاضی (۳)',
      'زیست‌شناسی (۳)',
      'فیزیک (۳)',
      'شیمی (۳)',
      'هویت اجتماعی',
      'سلامت و بهداشت',
    ],
    sessionsPerDay: 6, sessionDuration: 70, breakDuration: 15, weeklyHours: 10, gradeType: 'high', field: 'تجربی'
  },

  // ===== دهم علوم انسانی =====
  'پایه دهم انسانی': {
    books: [
      'فارسی (۱)',
      'دین و زندگی (۱)',
      'عربی، زبان قرآن (۱)',
      'زبان انگلیسی (۱)',
      'ریاضی و آمار (۱)',
      'علوم و فنون ادبی (۱)',
      'تاریخ (۱) — ایران و جهان باستان',
      'جغرافیای ایران',
      'جامعه‌شناسی (۱)',
      'اقتصاد',
      'منطق',
    ],
    sessionsPerDay: 5, sessionDuration: 55, breakDuration: 12, weeklyHours: 7, gradeType: 'high', field: 'انسانی'
  },
  'پایه یازدهم انسانی': {
    books: [
      'فارسی (۲)',
      'دین و زندگی (۲)',
      'عربی، زبان قرآن (۲)',
      'زبان انگلیسی (۲)',
      'ریاضی و آمار (۲)',
      'علوم و فنون ادبی (۲)',
      'تاریخ (۲)',
      'جغرافیا (۲)',
      'جامعه‌شناسی (۲)',
      'روان‌شناسی',
      'فلسفه (۱)',
    ],
    sessionsPerDay: 5, sessionDuration: 60, breakDuration: 12, weeklyHours: 8, gradeType: 'high', field: 'انسانی'
  },
  'پایه دوازدهم انسانی': {
    books: [
      'فارسی (۳)',
      'دین و زندگی (۳)',
      'عربی، زبان قرآن (۳)',
      'زبان انگلیسی (۳)',
      'ریاضی و آمار (۳)',
      'علوم و فنون ادبی (۳)',
      'تاریخ (۳)',
      'جغرافیا (۳)',
      'جامعه‌شناسی (۳)',
      'فلسفه (۲)',
      'هویت اجتماعی',
      'سلامت و بهداشت',
    ],
    sessionsPerDay: 5, sessionDuration: 60, breakDuration: 15, weeklyHours: 8.5, gradeType: 'high', field: 'انسانی'
  }
};

const DAYS = ['شنبه', 'یک‌شنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];
const DAYS_SHORT = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

// ============================================================
// موتور برنامه‌ریزی قطعی
// ============================================================
const SCHEDULER_VERSION = '5.0.0';
const MIN_SESSION_MINUTES = 15;
const MAX_SESSION_MINUTES = 120;
const DEFAULT_ACTIVITY_DURATION = 60;
const DEFAULT_MEAL_DURATIONS = { breakfast: 30, lunch: 30, dinner: 30 };
const DEFAULT_SCHOOL_START_TIME = '07:00';
const DEFAULT_SCHOOL_END_TIME = '14:00';
const DEFAULT_AFTER_SCHOOL_COMMUTE = 30;
const MIN_RECOMMENDED_SLEEP_HOURS = { elementary: 9, middle: 9, high: 8 };

const SCHOOL_PERIODS = [
  { name: 'زنگ اول', start: 7 * 60 + 30, end: 8 * 60 + 15 },
  { name: 'زنگ دوم', start: 8 * 60 + 25, end: 9 * 60 + 10 },
  { name: 'زنگ سوم', start: 9 * 60 + 20, end: 10 * 60 + 5 },
  { name: 'زنگ چهارم', start: 10 * 60 + 15, end: 11 * 60 },
  { name: 'زنگ پنجم', start: 11 * 60 + 10, end: 11 * 60 + 55 },
  { name: 'زنگ ششم', start: 12 * 60 + 5, end: 12 * 60 + 50 },
];

const SUBJECT_TYPE_RULES = {
  elementary: {
    maxSession: 90,
    heavyMax: 90,
    lightMax: 60,
    dailyDistinctSubjects: 20,
  },
  middle: {
    maxSession: 120,
    heavyMax: 120,
    lightMax: 90,
    dailyDistinctSubjects: 20,
  },
  high: {
    maxSession: 150,
    heavyMax: 150,
    lightMax: 90,
    dailyDistinctSubjects: 20,
  },
};

const FARSIDIGIT_MAP = { '۰':'0','۱':'1','۲':'2','۳':'3','۴':'4','۵':'5','۶':'6','۷':'7','۸':'8','۹':'9' };
const ARABICDIGIT_MAP = { '٠':'0','١':'1','٢':'2','٣':'3','٤':'4','٥':'5','٦':'6','٧':'7','٨':'8','٩':'9' };

const normalizeDigits = (value) => String(value ?? '').replace(/[۰-۹]/g, c => FARSIDIGIT_MAP[c]).replace(/[٠-٩]/g, c => ARABICDIGIT_MAP[c]);
const normalizeText = (value) => String(value ?? '').trim().replace(/ي/g, 'ی').replace(/ك/g, 'ک').replace(/\s+/g, ' ');

const parseClockMinutes = (value) => {
  const raw = normalizeDigits(value).trim();
  const match = raw.match(/^(\d{1,2})[:٫.](\d{1,2})$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || h > 24 || m < 0 || m > 59) return null;
  if (h === 24 && m !== 0) return null;
  return h * 60 + m;
};

const formatClock = (minutes) => {
  const m = Math.max(0, Math.min(1440, Math.round(minutes)));
  if (m === 1440) return '24:00';
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

const parseTimeRange = (timeValue, defaultDuration = DEFAULT_ACTIVITY_DURATION) => {
  const raw = normalizeDigits(timeValue);
  const parts = raw.split(/\s*(?:-|–|—|تا|to)\s*/i).filter(Boolean);
  const start = parseClockMinutes(parts[0]);
  if (start == null) return null;
  if (parts.length < 2) {
    return { start, end: Math.min(1440, start + defaultDuration) };
  }
  const end = parseClockMinutes(parts[1]);
  if (end == null || end <= start) return null;
  return { start, end };
};

const intervalOverlap = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && aEnd > bStart;

const mergeIntervals = (intervals) => {
  const sorted = intervals
    .filter(i => Number.isFinite(i.start) && Number.isFinite(i.end) && i.end > i.start)
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const merged = [];
  for (const item of sorted) {
    const last = merged[merged.length - 1];
    if (!last || item.start > last.end) {
      merged.push({ ...item });
    } else {
      last.end = Math.max(last.end, item.end);
      last.labels = [...new Set([...(last.labels || []), ...(item.labels || [])])];
      last.types = [...new Set([...(last.types || []), ...(item.types || [])])];
    }
  }
  return merged;
};

const normalizeDayIndex = (day) => {
  const text = normalizeText(day);
  const idx = DAYS.findIndex(d => d === text || d.replace('‌', '') === text.replace('‌', ''));
  return idx >= 0 ? idx : null;
};

const getStudyDaySet = (studyDays) => {
  const v = normalizeText(studyDays || 'همه روزها');
  if (v.includes('شنبه تا چهارشنبه')) return new Set([0,1,2,3,4]);
  if (v.includes('شنبه تا پنجشنبه')) return new Set([0,1,2,3,4,5]);
  return new Set([0,1,2,3,4,5,6]);
};

const getHolidaySet = () => new Set();

const getSubjectProfile = (book, gradeType, field = '') => {
  const name = normalizeText(book);
  const heavy = /(ریاضی|حسابان|هندسه|آمار|فیزیک|شیمی|زیست|علوم تجربی|علوم و فنون)/.test(name);
  const memorization = /(فارسی|ادبیات|دین و زندگی|پیام|عربی|زبان|تاریخ|جغرافیا|جامعه|روانشناسی|فلسفه|سلامت|هویت|قرآن|هدیه|مطالعات)/.test(name);
  const elementary = gradeType === 'elementary';
  let type = heavy ? 'analytical' : memorization ? 'memory' : 'mixed';
  if (elementary) type = heavy ? 'analytical' : 'elementary';
  return { type, field, weight: heavy ? 1.25 : memorization ? 1 : 0.9 };
};

const getAdaptiveSessionMinutes = ({ gradeType, subject, activity, requested, remaining }) => {
  const rules = SUBJECT_TYPE_RULES[gradeType] || SUBJECT_TYPE_RULES.middle;
  const profile = getSubjectProfile(subject, gradeType);
  let upper = Number.isFinite(requested) ? requested : rules.maxSession;
  if (requested === 'ترکیبی' || requested === 'combined' || !Number.isFinite(requested)) {
    if (remaining >= 90) upper = 90;
    else if (remaining >= 75) upper = 75;
    else if (remaining >= 60) upper = 60;
    else if (remaining >= 45) upper = 45;
    else if (remaining >= 30) upper = 30;
    else upper = Math.max(MIN_SESSION_MINUTES, remaining);
    if (profile.type === 'analytical' && remaining >= 60) upper = Math.min(upper, 90);
    if (profile.type === 'memory' && remaining >= 30) upper = Math.min(upper, 60);
    if (/test|آزمون|تست|شبیه/.test(String(activity || ''))) upper = Math.min(rules.maxSession, upper);
  }
  if (/elementary/.test(gradeType)) upper = Math.min(upper, 30);
  upper = Math.max(MIN_SESSION_MINUTES, Math.min(MAX_SESSION_MINUTES, upper));
  return Math.max(MIN_SESSION_MINUTES, Math.min(upper, remaining));
};

const safeTopic = (topic, subject, activity) => {
  let t = normalizeText(topic);
  if (!t || t.length < 2) {
    const fallbackByType = {
      learn: `مطالعه بخش مشخص‌شده از ${subject}`,
      practice: `تمرین مباحث اخیر ${subject}`,
      retrieval: `بازیابی فعال مباحث اخیر ${subject}`,
      review: `مرور فاصله‌دار مباحث اخیر ${subject}`,
      test: `آزمون مباحث اخیر ${subject}`,
      test_analysis: `تحلیل خطاهای آزمون ${subject}`,
      catch_up: `جبران عقب‌ماندگی‌های ${subject}`,
    };
    return fallbackByType[activity] || `مطالعه ${subject}`;
  }
  t = t.replace(/\bفصل\s*\d+\s*[:：-]?\s*/g, '').replace(/\bدرس\s*\d+\s*[:：-]?\s*/g, '');
  return t || `مطالعه بخش مشخص‌شده از ${subject}`;
};

const normalizeActivity = (activity, goal, gradeType) => {
  const raw = normalizeText(activity).toLowerCase();
  if (/(جبران|catch)/.test(raw)) return 'catch_up';
  if (/(تحلیل|error|خطا)/.test(raw)) return 'test_analysis';
  if (/(آزمون|تست|test|شبیه)/.test(raw)) return 'test';
  if (/(مرور|review|retrieval|بازیابی)/.test(raw)) return goal === 'مرور درس ها' ? 'retrieval' : 'review';
  if (/(تمرین|حل|practice)/.test(raw)) return 'practice';
  return gradeType === 'elementary' ? 'learn' : 'learn';
};

const normalizeEducationalTasks = (rawTasks, context) => {
  const selected = context.selectedBooks;
  const tasks = Array.isArray(rawTasks) ? rawTasks : [];
  const normalized = tasks.map((task, index) => {
    const requestedSubject = normalizeText(task?.subject || task?.book);
    const subject = selected.find(b => normalizeText(b) === requestedSubject) || selected.find(b => requestedSubject.includes(normalizeText(b))) || null;
    if (!subject) return null;
    const activity = normalizeActivity(task?.activity || task?.sessionType || 'learn', context.goal, context.gradeType);
    const userPriority = Number(context.subjectPriority?.[subject]);
    const priority = Math.max(1, Math.min(10, Number.isFinite(userPriority) && userPriority > 0 ? userPriority + 2 : (Number(task?.priority) || 5)));
    const estimated = Math.max(MIN_SESSION_MINUTES, Math.min(180, Number(task?.estimatedMinutes) || 45));
    const userTestCount = Number(context.testsBySubject?.[subject]);
    const questionCount = context.gradeType === 'elementary'
      ? 0
      : Math.max(0, Math.min(60, Number.isFinite(userTestCount) ? userTestCount : (Number(task?.questionCount ?? task?.testCount) || 0)));
    const repeatable = task?.repeatable !== false;
    return {
      id: String(task?.id || `task_${index + 1}`),
      subject,
      topic: safeTopic(task?.topic, subject, activity),
      activity,
      priority,
      estimatedMinutes: estimated,
      questionCount,
      repeatable,
      rationale: normalizeText(task?.rationale || task?.reason || ''),
      examRelated: Boolean(task?.examRelated),
    };
  }).filter(Boolean);

  const bySubject = new Map(selected.map(s => [s, false]));
  normalized.forEach(t => bySubject.set(t.subject, true));
  for (const subject of selected) {
    if (bySubject.get(subject)) continue;
    const profile = getSubjectProfile(subject, context.gradeType, context.field);
    normalized.push({
      id: `auto_${subject}`,
      subject,
      topic: safeTopic('', subject, profile.type === 'analytical' ? 'practice' : 'learn'),
      activity: profile.type === 'analytical' ? 'practice' : 'learn',
      priority: 4,
      estimatedMinutes: context.gradeType === 'elementary' ? 25 : 45,
      questionCount: context.gradeType === 'elementary' ? 0 : Math.min(context.testsBySubject?.[subject] || 10, 20),
      repeatable: true,
      rationale: 'پوشش حداقل یک نوبت از درس انتخاب‌شده',
      examRelated: false,
    });
  }
  return normalized;
};

{/*const buildDailyTargetMinutes = (context, dayIndex) => {
  if (!context.studyDays.has(dayIndex) || context.restDays.has(dayIndex)) return 0;
  return Math.round(((context.isHoliday(dayIndex) ? context.studyHoursHolidays : context.studyHoursSchoolDays) || 0) * 60);
};*/}
const buildDailyTargetMinutes = (context, dayIndex) => {
  if (!context.studyDays.has(dayIndex) || context.restDays.has(dayIndex)) return 0;
  
  // ۱. هدف اولیه بر اساس تنظیمات کاربر
  let target = Math.round(((context.isHoliday(dayIndex) ? context.studyHoursHolidays : context.studyHoursSchoolDays) || 0) * 60);
  
  // ۲. محاسبه‌ی ظرفیت واقعی روز (با در نظر گرفتن جلسات و استراحت‌ها)
  const detail = describeDayCapacity(context, dayIndex);
  const capacity = detail.capacity;
  
  // ۳. اگر هدف از ظرفیت بیشتر است، آن را به capacity محدود کن
  if (target > capacity) {
    target = capacity;
  }
  
  return Math.round(target);
};

const getTimetableBlocks = (timetable, dayIndex) => {
  const blocks = [];
  const entries = Object.entries(timetable || {});
  for (const [key, value] of entries) {
    if (!value || !String(value).trim()) continue;
    const parts = key.split('_');
    if (parts.length < 3) continue;
    const idx = Number(parts[1]);
    if (idx !== dayIndex) continue;
    const periodName = parts.slice(2).join('_');
    const period = SCHOOL_PERIODS.find(p => p.name === periodName);
    if (period) blocks.push({
      start: period.start,
      end: period.end,
      type: 'school',
      title: normalizeText(value),
      labels: [normalizeText(value)],
      types: ['school'],
    });
  }
  return blocks;
};

const getInputActivityBlocks = (items, dayIndex, type) => {
  const blocks = [];
  for (const item of items || []) {
    const itemDay = normalizeDayIndex(item?.day);
    if (itemDay !== dayIndex) continue;
    const interval = item?.start != null && item?.end != null
      ? { start: parseClockMinutes(item.start), end: parseClockMinutes(item.end) }
      : parseTimeRange(item?.time, Number(item?.duration) || DEFAULT_ACTIVITY_DURATION);
    if (!interval || interval.start == null || interval.end == null || interval.end <= interval.start) continue;
    blocks.push({
      start: interval.start,
      end: interval.end,
      type,
      title: normalizeText(item?.name || item?.title || type),
      labels: [normalizeText(item?.name || item?.title || type)],
      types: [type],
    });
  }
  return blocks;
};

const buildFixedBlocks = (context, dayIndex) => {
  const blocks = [];
  const wake = parseClockMinutes(context.wakeUpTime);
  const sleep = parseClockMinutes(context.sleepTime);

  // خواب شبانه
  if (wake != null) blocks.push({ start: 0, end: wake, type: 'sleep', title: 'خواب', labels: ['خواب'], types: ['sleep'] });
  if (sleep != null && sleep > 0) {
    blocks.push({ start: sleep, end: 1440, type: 'sleep', title: 'خواب', labels: ['خواب'], types: ['sleep'] });
  }

  // آماده‌سازی صبح (۳۰ دقیقه بعد از بیداری)
  if (wake != null) {
    const routineEnd = Math.min(1440, wake + 30);
    blocks.push({
      start: wake,
      end: routineEnd,
      type: 'morning_routine',
      title: 'آماده‌سازی صبح',
      labels: ['آماده‌سازی صبح'],
      types: ['morning_routine']
    });
  }

  // ===== ۱. محاسبه زمان ناهار (پویا بر اساس روز و شیفت) =====
  let lunchStart = parseClockMinutes(context.lunchTime);
  const isHoliday = context.isHoliday(dayIndex);
  const schoolStart = parseClockMinutes(context.schoolStartTime);
  const schoolEnd = parseClockMinutes(context.schoolEndTime);
  const commute = Number(context.afterSchoolCommuteMinutes) || 0;

  // تشخیص شیفت عصر: اگر مدرسه از ۱۲:۰۰ یا بعد شروع شود
  const isEveningShift = schoolStart != null && schoolStart >= 12 * 60;

  if (!isHoliday && schoolStart != null && schoolEnd != null) {
    if (isEveningShift) {
      // شیفت عصر: اگر ناهار در محدوده مدرسه است، آن را قبل از مدرسه قرار بده
      if (lunchStart == null || (lunchStart >= schoolStart && lunchStart < schoolEnd)) {
        lunchStart = Math.max(0, schoolStart - 30);
      }
    } else {
      // شیفت صبح: اگر ناهار قبل از رسیدن به خانه است، آن را به زمان رسیدن منتقل کن
      const arrival = schoolEnd + commute;
      if (lunchStart == null || lunchStart < arrival) {
        lunchStart = arrival;
      }
    }
  }

  if (lunchStart == null) {
    lunchStart = parseClockMinutes('12:30') || 750; // 12:30
  }

  // ===== ۲. وعده‌های غذایی =====
  const mealEntries = [
    ['breakfast', context.breakfastTime, context.breakfastDuration, 'صبحانه'],
    ['lunch', lunchStart, context.lunchDuration, 'ناهار'],
    ['dinner', context.dinnerTime, context.dinnerDuration, 'شام'],
  ];
  for (const [, value, duration, title] of mealEntries) {
    const start = typeof value === 'number' ? value : parseClockMinutes(value);
    const dur = Math.max(15, Number(duration) || 30);
    if (start != null) {
      blocks.push({
        start: start,
        end: Math.min(1440, start + dur),
        type: 'meal',
        title: title,
        labels: [title],
        types: ['meal']
      });
    }
  }

  // ===== ۳. خواب نیمروزی (بلافاصله بعد از ناهار) =====
  const lunchDur = Math.max(15, Number(context.lunchDuration) || 30);
  const napDuration = Number(context.afterSchoolRestDuration) || 0;
  if (napDuration > 0 && lunchStart != null) {
    const napStart = Math.min(1440, lunchStart + lunchDur);
    const napEnd = Math.min(1440, napStart + napDuration);
    if (napEnd > napStart) {
      blocks.push({
        start: napStart,
        end: napEnd,
        type: 'rest',
        title: 'خواب نیمروزی',
        labels: ['خواب نیمروزی'],
        types: ['rest']
      });
    }
  }

  // ===== ۴. مدرسه =====
  if (!isHoliday) {
    const timetableBlocks = getTimetableBlocks(context.timetable, dayIndex);
    const inferredEnd = timetableBlocks.length ? Math.max(...timetableBlocks.map(x => x.end)) : null;
    const schoolStartFinal = schoolStart ?? 450;
    const schoolEndFinal = schoolEnd ?? inferredEnd;

    if (schoolEndFinal != null && schoolEndFinal > schoolStartFinal) {
      blocks.push({ start: schoolStartFinal, end: schoolEndFinal, type: 'school', title: 'مدرسه', labels: ['مدرسه'], types: ['school'] });
      if (commute > 0) {
        blocks.push({
          start: schoolEndFinal,
          end: Math.min(1440, schoolEndFinal + commute),
          type: 'commute',
          title: 'رفت‌وآمد بعد از مدرسه',
          labels: ['رفت‌وآمد بعد از مدرسه'],
          types: ['commute'],
        });
      }
    }
  }

  // کلاس‌های فوق‌برنامه و فعالیت‌های خارج از برنامه
  blocks.push(...getInputActivityBlocks(context.extraClasses, dayIndex, 'extra_class'));
  blocks.push(...getInputActivityBlocks(context.outsideActivities, dayIndex, 'activity'));
  return mergeIntervals(blocks);
};

const getPreferredTimeBands = (studyTime) => {
  const v = normalizeText(studyTime || 'ترکیبی');
  if (v === 'صبح') return [{ start: 6 * 60, end: 12 * 60 }];
  if (v === 'عصر') return [{ start: 14 * 60, end: 22 * 60 }];
  if (v === 'شب') return [{ start: 18 * 60, end: 24 * 60 }];
  return [{ start: 6 * 60, end: 24 * 60 }];
};

const intersectWithPreference = (windows, studyTime) => {
  const bands = getPreferredTimeBands(studyTime);
  const result = [];
  for (const w of windows) {
    for (const band of bands) {
      const start = Math.max(w.start, band.start);
      const end = Math.min(w.end, band.end);
      if (end > start) result.push({ start, end });
    }
  }
  return result;
};

const getFreeWindows = (fixedBlocks, studyTime = 'ترکیبی') => {
  const merged = mergeIntervals(fixedBlocks);
  const windows = [];
  let cursor = 0;
  for (const block of merged) {
    if (block.start > cursor) windows.push({ start: cursor, end: block.start });
    cursor = Math.max(cursor, block.end);
  }
  if (cursor < 1440) windows.push({ start: cursor, end: 1440 });
  return intersectWithPreference(windows.filter(w => w.end - w.start >= MIN_SESSION_MINUTES), studyTime)
    .filter(w => w.end - w.start >= MIN_SESSION_MINUTES);
};

const taskScoreForDay = (task, ctx, dayIndex, recentSubjectDays) => {
  let score = task.priority * 10;
  if (task.examRelated) score += 25;
  const targetExamDay = ctx.examDaysBySubject?.[task.subject];
  if (targetExamDay != null) {
    const distance = targetExamDay - dayIndex;
    if (distance >= 0 && distance <= 2) score += 35 - distance * 10;
    if (distance < 0) score -= 10;
  }
  if (ctx.weakSubjects.has(task.subject)) score += 20;
  if (ctx.strongSubjects?.has(task.subject)) score -= 4;
  const distribution = Number(ctx.subjectDistribution?.[task.subject]) || 0;
  if (distribution > 0) score += Math.min(25, distribution / 4);
  if (ctx.studyTime === 'صبح' && dayIndex >= 0) score += 1;
  if (recentSubjectDays.has(task.subject)) score -= 16;
  if (task.activity === 'review' || task.activity === 'retrieval') score += 8;
  return score;
};

const chooseTask = (tasks, context, dayIndex, usedSubjectsToday, recentSubjectDays, schedulerState) => {
  const { learnedLastDay, lastAnyDay, lastReviewDay } = schedulerState;

  // فیلتر تسک‌های واجد شرایط
  const eligible = tasks.filter(t => {
    if (!(t.remainingMinutes > 0)) return false;
    const isReview = t.activity === 'review' || t.activity === 'retrieval';
    if (isReview) {
      const lastLearningDay = learnedLastDay.get(t.subject);
      if (lastLearningDay == null || dayIndex <= lastLearningDay) return false;
      const previousReview = lastReviewDay.get(t.subject);
      if (previousReview != null && dayIndex - previousReview < 2) return false;
    } else {
      const previousDay = lastAnyDay.get(t.subject);
      if (previousDay != null && dayIndex - previousDay < 2) return false;
    }
    return true;
  });

  const candidates = eligible.length ? eligible : tasks.filter(t => t.remainingMinutes > 0);
  if (candidates.length === 0) return null;

  // نقشه‌ی اولویت هر درس
  const subjectPriorityMap = {};
  for (const t of tasks) {
    if (!subjectPriorityMap[t.subject]) {
      subjectPriorityMap[t.subject] = t.priority;
    }
  }

  // شمارش اولویت‌های استفاده‌شده در امروز
  const priorityCounts = { 1: 0, 2: 0, 3: 0 };
  for (const subject of usedSubjectsToday) {
    const p = subjectPriorityMap[subject] || 2;
    priorityCounts[p] = (priorityCounts[p] || 0) + 1;
  }

  // شمارش تکرار هر درس در امروز
  const subjectRepeatCount = {};
  for (const subject of usedSubjectsToday) {
    subjectRepeatCount[subject] = (subjectRepeatCount[subject] || 0) + 1;
  }

  // امتیازدهی
  const scored = candidates.map(task => {
    let score = taskScoreForDay(task, context, dayIndex, recentSubjectDays)
      + (usedSubjectsToday.has(task.subject) ? -3 : 3)
      + (task.activity === 'learn' ? 5 : 0)
      + (task.activity === 'review' || task.activity === 'retrieval' ? 3 : 0);

    const p = task.priority;
    const repeatCount = subjectRepeatCount[task.subject] || 0;
    let diversityBonus = 0;

    // جلوگیری از تکرار یک درس در روز
    if (repeatCount >= 1) {
      diversityBonus -= 25 * repeatCount;
    }

    // تشویق درس‌هایی که چند روز است انتخاب نشده‌اند
    if (!recentSubjectDays.has(task.subject)) {
      diversityBonus += 20;
    }

    // تنوع اولویت‌ها
    const totalSessionsToday = usedSubjectsToday.size;

    if (p === 3 && priorityCounts[3] === 0) {
      diversityBonus += 80;
    } else if (p === 3 && priorityCounts[3] === 1) {
      diversityBonus += 20;
    } else if (p === 3 && priorityCounts[3] >= 2) {
      if (totalSessionsToday < 5) {
        diversityBonus -= 30;
      } else {
        diversityBonus -= 10;
      }
    }

    if (p === 1 && priorityCounts[1] === 0 && priorityCounts[3] > 0) {
      diversityBonus += 60;
    } else if (p === 1 && priorityCounts[1] === 1 && priorityCounts[3] > 0) {
      diversityBonus += 20;
    } else if (p === 1 && priorityCounts[1] >= 2) {
      diversityBonus -= 20;
    }

    if (priorityCounts[3] >= 2 && priorityCounts[1] === 0 && p === 1) {
      diversityBonus += 40;
    }

    if (priorityCounts[1] >= 2 && priorityCounts[3] === 0 && p === 3) {
      diversityBonus += 40;
    }

    score += diversityBonus;
    return { task, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.task || null;
};

const allocateTaskMinutes = (task, minutes, context) => {
  if (!task || minutes <= 0) return null;
  const sessionType = task.activity;
  let questionCount = task.questionCount || 0;
  if (context.gradeType === 'elementary') questionCount = 0;
  if (sessionType !== 'test' && sessionType !== 'test_analysis') {
    questionCount = Math.min(questionCount, Math.max(0, Math.floor(minutes / 4)));
  }
  let learningMethod = 'concept_learning';
  if (sessionType === 'practice') learningMethod = 'worked_examples_and_practice';
  if (sessionType === 'retrieval') learningMethod = 'active_recall';
  if (sessionType === 'review') learningMethod = 'spaced_review';
  if (sessionType === 'test') learningMethod = 'timed_questions';
  if (sessionType === 'test_analysis') learningMethod = 'error_analysis';
  if (context.gradeType === 'elementary') learningMethod = 'game_based_or_hands_on_learning';
  return {
    book: task.subject,
    topic: task.topic,
    time: '',
    topicId: task.topicId || null,
    sessionType,
    studyMinutes: minutes,
    testCount: questionCount,
    learningMethod,
    priority: task.priority,
    reason: task.rationale || '',
  };
};

const planDaySessions = (dayIndex, context, tasks, fixedBlocks, targetMinutes, recentSubjectDays, schedulerState) => {
  if (targetMinutes <= 0) return { sessions: [], remainingTarget: 0 };

  // دریافت پنجره‌های آزاد مرتب
  const freeWindows = getFreeWindows(fixedBlocks, context.studyTime)
    .slice()
    .sort((a, b) => a.start - b.start);

  if (freeWindows.length === 0) return { sessions: [], remainingTarget: targetMinutes };

  const tempSessions = [];
  let remainingTarget = targetMinutes;
  const requested = context.sessionDuration === 'ترکیبی' ? 'ترکیبی' : Number(context.sessionDuration);
  const usedSubjectsToday = new Set();
  let fallbackCounter = 0;
  const activityTypes = ['learn', 'practice', 'review', 'retrieval', 'test'];
  const possibleDurations = [30, 45, 60, 75, 90];

  // تولید جلسات در تمام پنجره‌ها
  for (const window of freeWindows) {
    let cursor = window.start;
    while (remainingTarget > 0 && cursor < window.end) {
      const remainingWindow = window.end - cursor;
      if (remainingWindow < MIN_SESSION_MINUTES) break;

      let maxStudy = getAdaptiveSessionMinutes({
        gradeType: context.gradeType,
        subject: 'درس',
        activity: 'learn',
        requested,
        remaining: Math.min(remainingTarget, remainingWindow),
      });
      maxStudy = Math.min(maxStudy, remainingWindow, remainingTarget);
      if (maxStudy < MIN_SESSION_MINUTES) break;

      let task = chooseTask(tasks, context, dayIndex, usedSubjectsToday, recentSubjectDays, schedulerState);
      
      if (!task) {
        fallbackCounter++;
        const subjectIndex = fallbackCounter % context.selectedBooks.length;
        const fallbackSubject = context.selectedBooks[subjectIndex] || 'مطالعه';
        const activityIndex = fallbackCounter % activityTypes.length;
        const activity = activityTypes[activityIndex];
        let duration = 45;
        for (const d of possibleDurations) {
          if (d <= Math.min(maxStudy, remainingTarget)) duration = d;
          else break;
        }
        if (duration < MIN_SESSION_MINUTES) duration = Math.min(maxStudy, 60);
        const topics = [
          `مطالعه و مرور ${fallbackSubject}`,
          `تمرین مباحث ${fallbackSubject}`,
          `جمع‌بندی ${fallbackSubject}`,
          `حل سوالات ${fallbackSubject}`,
          `مرور سریع ${fallbackSubject}`
        ];
        const topic = topics[fallbackCounter % topics.length];
        task = {
          id: `fallback_${Date.now()}_${fallbackCounter}`,
          subject: fallbackSubject,
          topic: topic,
          activity: activity,
          remainingMinutes: duration,
          priority: 1,
          repeatable: true,
          rationale: `جلسه تکمیلی`,
          questionCount: 0,
          examRelated: false,
        };
        tasks.push(task);
      }

      const subjectSpecificMax = getAdaptiveSessionMinutes({
        gradeType: context.gradeType,
        subject: task.subject,
        activity: task.activity,
        requested,
        remaining: Math.min(remainingTarget, remainingWindow),
      });
      let duration = Math.min(maxStudy, subjectSpecificMax);
      if (requested === 'ترکیبی') {
        let bestDuration = 45;
        for (const d of possibleDurations) {
          if (d <= duration) bestDuration = d;
        }
        duration = bestDuration;
      } else {
        duration = Math.min(duration, Number(requested));
      }
      if (duration < MIN_SESSION_MINUTES) break;
      if (context.gradeType === 'elementary') duration = Math.min(duration, 30);
      duration = Math.min(duration, remainingTarget);

      const end = cursor + duration;
      if (end > window.end) break;

      const session = allocateTaskMinutes(task, duration, context);
      if (!session) break;
      session.time = `${formatClock(cursor)} - ${formatClock(end)}`;
      session.duration = duration;
      session.done = false;
      session.isFixed = false;
      session.dayIndex = dayIndex;
      session.priority = task.priority;

      tempSessions.push(session);

      task.remainingMinutes -= duration;
      remainingTarget -= duration;
      usedSubjectsToday.add(task.subject);
      recentSubjectDays.delete(task.subject);
      recentSubjectDays.add(task.subject);
      
      if (task.activity !== 'review' && task.activity !== 'retrieval' && task.activity !== 'test_analysis') {
        schedulerState.learnedLastDay.set(task.subject, dayIndex);
      }
      schedulerState.lastAnyDay.set(task.subject, dayIndex);
      if (task.activity === 'review' || task.activity === 'retrieval') {
        schedulerState.lastReviewDay.set(task.subject, dayIndex);
      }

      cursor = end;
      if (remainingTarget <= 0) break;
      
      const isHoliday = context.isHoliday(dayIndex);
      const baseBreak = context.breakDuration || 15;
      const extraBreak = isHoliday ? Math.floor(Math.random() * 21) + 10 : Math.floor(Math.random() * 16);
      const breakTime = baseBreak + extraBreak;
      const nextCursor = cursor + breakTime;
      if (nextCursor >= window.end) break;
      cursor = nextCursor;
      if (window.end - cursor < MIN_SESSION_MINUTES) break;
    }
  }

  // مرتب‌سازی همه جلسات بر اساس اولویت
  tempSessions.sort((a, b) => (b.priority || 1) - (a.priority || 1));

  // توزیع جلسات در پنجره‌های آزاد به‌همین ترتیب
  const finalSessions = [];
  let sessionIndex = 0;
  let currentWindowIndex = 0;

  while (sessionIndex < tempSessions.length && currentWindowIndex < freeWindows.length) {
    const window = freeWindows[currentWindowIndex];
    let cursor = window.start;

    while (sessionIndex < tempSessions.length && cursor < window.end) {
      const session = tempSessions[sessionIndex];
      const duration = session.duration;

      if (cursor + duration > window.end) {
        break;
      }

      session.time = `${formatClock(cursor)} - ${formatClock(cursor + duration)}`;
      finalSessions.push(session);
      sessionIndex++;
      cursor += duration;

      if (sessionIndex < tempSessions.length) {
        const isHoliday = context.isHoliday(dayIndex);
        const baseBreak = context.breakDuration || 15;
        const extraBreak = isHoliday ? Math.floor(Math.random() * 21) + 10 : Math.floor(Math.random() * 16);
        const breakTime = baseBreak + extraBreak;
        cursor += breakTime;
      }
    }
    currentWindowIndex++;
  }

  while (sessionIndex < tempSessions.length) {
    finalSessions.push(tempSessions[sessionIndex]);
    sessionIndex++;
  }

  finalSessions.sort((a, b) => {
    const ar = parseTimeRange(a.time, a.duration || 45);
    const br = parseTimeRange(b.time, b.duration || 45);
    return (ar?.start ?? 0) - (br?.start ?? 0);
  });

  return { sessions: finalSessions, remainingTarget };
};

const buildReviewTasks = (baseTasks, context) => {
  const reviews = [];
  for (const base of baseTasks) {
    const count = Math.max(0, Number(context.reviewSessionsPerWeek?.[base.subject]) || 0);
    for (let i = 0; i < count; i++) {
      reviews.push({
        ...base,
        id: `${base.id}_review_${i + 1}`,
        activity: i === 0 ? 'review' : 'retrieval',
        estimatedMinutes: Math.min(base.estimatedMinutes || 45, context.gradeType === 'elementary' ? 25 : 45),
        remainingMinutes: Math.min(base.estimatedMinutes || 45, context.gradeType === 'elementary' ? 25 : 45),
        priority: Math.min(10, base.priority + 1),
        rationale: 'مرور فاصله‌دار بر اساس تنظیمات کاربر',
      });
    }
  }
  return reviews;
};

const expandTasksToWeeklyNeed = (baseTasks, context, weeklyMinutes) => {
  const base = normalizeEducationalTasks(baseTasks, context).map(t => {
    const nominal = context.sessionDuration === 'ترکیبی' ? null : Number(context.sessionDuration);
    const minDesired = nominal ? Math.min(nominal, MAX_SESSION_MINUTES) : (t.estimatedMinutes || 45);
    return { ...t, remainingMinutes: Math.max(MIN_SESSION_MINUTES, Math.max(t.estimatedMinutes || 0, minDesired)) };
  });
  const reviewTasks = buildReviewTasks(base, context).map(t => {
    const nominal = context.sessionDuration === 'ترکیبی' ? null : Number(context.sessionDuration);
    const desired = nominal ? Math.min(nominal, MAX_SESSION_MINUTES) : (t.estimatedMinutes || 45);
    return { ...t, remainingMinutes: Math.max(MIN_SESSION_MINUTES, Math.max(t.estimatedMinutes || 0, desired)) };
  });
  const all = [...base, ...reviewTasks];
  let total = all.reduce((sum, t) => sum + (t.remainingMinutes || 0), 0);
  let cursor = 0;
  while (total < weeklyMinutes && all.length) {
    const src = all[cursor % all.length];
    const nominal = context.sessionDuration === 'ترکیبی' ? Math.max(30, src.estimatedMinutes || 45) : Number(context.sessionDuration);
    const clone = {
      ...src,
      id: `${src.id}_repeat_${Math.floor(cursor / all.length) + 1}`,
      activity: src.activity === 'learn' ? 'practice' : src.activity,
      remainingMinutes: Math.min(Math.max(MIN_SESSION_MINUTES, nominal), weeklyMinutes - total),
      rationale: src.rationale || 'تقویت و تثبیت یادگیری',
    };
    if (clone.remainingMinutes > 0) {
      all.push(clone);
      total += clone.remainingMinutes;
    }
    cursor += 1;
    if (cursor > 500) break;
  }
  return all;
};

const normalizeExamMap = (exams) => {
  const map = {};
  for (const exam of exams || []) {
    const day = normalizeDayIndex(exam?.day);
    if (day == null) continue;
    const subject = normalizeText(exam?.book);
    if (!subject) continue;
    map[subject] = Math.max(map[subject] ?? -1, day);
  }
  return map;
};

const computeCapacity = (freeWindows, nominalSession, breakDuration) => {
  const sessionCap = Math.max(MIN_SESSION_MINUTES, Math.min(MAX_SESSION_MINUTES, Number(nominalSession) || 60));
  const rest = Math.max(0, Number(breakDuration) || 0);
  let capacity = 0;

  for (const window of freeWindows) {
    let length = Math.max(0, window.end - window.start);
    if (length < MIN_SESSION_MINUTES) continue;

    let fullSessions = Math.floor((length + rest) / (sessionCap + rest));
    if (fullSessions < 1 && length >= sessionCap) fullSessions = 1;
    if (fullSessions > 0) {
      const used = fullSessions * sessionCap + Math.max(0, fullSessions - 1) * rest;
      if (used <= length) capacity += fullSessions * sessionCap;
      else fullSessions = Math.max(0, fullSessions - 1);
    }

    if (fullSessions > 0) {
      const used = fullSessions * sessionCap + Math.max(0, fullSessions - 1) * rest;
      const remainderAfterBreak = length - used - rest;
      if (remainderAfterBreak >= MIN_SESSION_MINUTES) {
        capacity += Math.min(sessionCap, remainderAfterBreak);
      }
    } else if (length >= MIN_SESSION_MINUTES) {
      capacity += Math.min(sessionCap, length);
    }
  }
  return Math.floor(Math.max(0, capacity));
};

const describeDayCapacity = (context, dayIndex) => {
  const fixed = buildFixedBlocks(context, dayIndex);
  const free = getFreeWindows(fixed, context.studyTime);
  const sessionCap = context.sessionDuration === 'ترکیبی'
    ? Math.max(MIN_SESSION_MINUTES, 60)
    : Number(context.sessionDuration) || 45;
  return {
    fixed,
    free,
    capacity: computeCapacity(free, sessionCap, context.breakDuration),
    rawFreeMinutes: free.reduce((sum, w) => sum + (w.end - w.start), 0),
  };
};

const preflightValidateCapacity = (context) => {
  const issues = [];
  const capacityByDay = {};
  let weeklyTarget = 0;
  let weeklyCapacity = 0;

  for (let d = 0; d < 7; d++) {
    const target = buildDailyTargetMinutes(context, d);
    const detail = describeDayCapacity(context, d);
    capacityByDay[d] = { target, ...detail };
    weeklyTarget += target;
    weeklyCapacity += detail.capacity;

    if (target > detail.capacity) {
      issues.push({
        code: 'DAILY_CAPACITY_INSUFFICIENT',
        day: DAYS[d],
        target,
        capacity: detail.capacity,
        rawFreeMinutes: detail.rawFreeMinutes,
        free: detail.free,
        fixed: detail.fixed,
        deficit: target - detail.capacity,
      });
    }
  }

  if (weeklyTarget > 0 && weeklyCapacity < weeklyTarget) {
    issues.push({
      code: 'WEEKLY_CAPACITY_INSUFFICIENT',
      target: weeklyTarget,
      capacity: weeklyCapacity,
      deficit: weeklyTarget - weeklyCapacity,
    });
  }

  return { valid: issues.length === 0, issues, capacityByDay, weeklyTarget, weeklyCapacity };
};

const validatePlanHardConstraints = (plan, context) => {
  const errors = [];
  if (!plan || !Array.isArray(plan.days) || plan.days.length !== 7) {
    errors.push('برنامه باید دقیقاً ۷ روز داشته باشد.');
    return { valid: false, errors };
  }
  const selected = new Set(context.selectedBooks);
  for (let d = 0; d < 7; d++) {
    const day = plan.days[d];
    const sessions = Array.isArray(day.sessions) ? day.sessions : [];
    const fixed = buildFixedBlocks(context, d);
    for (let i = 0; i < sessions.length; i++) {
      const s = sessions[i];
      const range = parseTimeRange(s.time, s.duration || 45);
      if (!range) {
        errors.push(`${DAYS[d]}: زمان جلسه نامعتبر است.`);
        continue;
      }
      const duration = range.end - range.start;
      if (!selected.has(normalizeText(s.book))) {
        errors.push(`${DAYS[d]}: درس «${s.book}» خارج از دروس انتخابی کاربر است.`);
      }
      if (duration < MIN_SESSION_MINUTES) {
        errors.push(`${DAYS[d]}: جلسه کوتاه‌تر از حد مجاز (${MIN_SESSION_MINUTES} دقیقه) است.`);
      }
      for (const block of fixed) {
        if (intervalOverlap(range.start, range.end, block.start, block.end)) {
          errors.push(`${DAYS[d]}: جلسه ${s.book} با ${block.title} تداخل دارد.`);
        }
      }
      for (let j = i + 1; j < sessions.length; j++) {
        const other = parseTimeRange(sessions[j].time, sessions[j].duration || 45);
        if (other && intervalOverlap(range.start, range.end, other.start, other.end)) {
          errors.push(`${DAYS[d]}: دو جلسه با هم تداخل دارند.`);
        }
      }
    }
  }
  return { valid: errors.length === 0, errors };
};

const repairPlan = (plan, context) => {
  const repaired = JSON.parse(JSON.stringify(plan));
  for (let d = 0; d < repaired.days.length; d++) {
    const sessions = repaired.days[d].sessions || [];
    const fixedBlocks = buildFixedBlocks(context, d);
    const freeWindows = getFreeWindows(fixedBlocks, context.studyTime);
    const sortedSessions = sessions.slice().sort((a, b) => {
      const aa = parseTimeRange(a.time, a.duration || 45)?.start ?? 0;
      const bb = parseTimeRange(b.time, b.duration || 45)?.start ?? 0;
      return aa - bb;
    });
    const placed = [];
    let windowIndex = 0;
    let cursor = freeWindows[0]?.start ?? 0;

    for (const session of sortedSessions) {
      const duration = Math.max(MIN_SESSION_MINUTES, Number(session.duration) || (parseTimeRange(session.time, 45)?.end - parseTimeRange(session.time, 45)?.start) || 45);
      let placedHere = false;
      while (windowIndex < freeWindows.length) {
        const window = freeWindows[windowIndex];
        const previousEnd = placed.length ? parseTimeRange(placed[placed.length - 1].time, placed[placed.length - 1].duration)?.end : null;
        const earliest = Math.max(window.start, previousEnd == null ? window.start : previousEnd + context.breakDuration, cursor);
        if (earliest + duration <= window.end) {
          session.time = `${formatClock(earliest)} - ${formatClock(earliest + duration)}`;
          session.duration = duration;
          placed.push(session);
          cursor = earliest + duration + context.breakDuration;
          placedHere = true;
          break;
        }
        windowIndex += 1;
        cursor = freeWindows[windowIndex]?.start ?? 0;
      }
      if (!placedHere) {
        placed.push(session);
      }
    }
    repaired.days[d].sessions = placed;
  }
  return repaired;
};

const buildCautiousFallbackTasks = (context) => {
  return context.selectedBooks.map((book, index) => {
    const profile = getSubjectProfile(book, context.gradeType, context.field);
    return {
      id: `fallback_${index + 1}`,
      subject: book,
      topic: safeTopic('', book, profile.type === 'analytical' ? 'practice' : 'learn'),
      activity: profile.type === 'analytical' ? 'practice' : 'learn',
      priority: Number(context.subjectPriority?.[book]) || 2,
      estimatedMinutes: context.gradeType === 'elementary' ? 25 : profile.type === 'analytical' ? 60 : 45,
      questionCount: context.gradeType === 'elementary' ? 0 : Number(context.testsBySubject?.[book]) || 10,
      repeatable: true,
      rationale: 'تولید ایمن در نبود خروجی آموزشی معتبر از مدل',
      examRelated: false,
    };
  });
};

const getGradeConfig = (grade, field = '') => {
  if (!grade) return SUBJECTS_DB['پایه هفتم'];
  const normalizedGrade = normalizeText(grade);
  const normalizedField = normalizeText(field);
  if (SUBJECTS_DB[normalizedGrade]) return SUBJECTS_DB[normalizedGrade];
  if (normalizedField && /دهم|یازدهم|دوازدهم/.test(normalizedGrade)) {
    const composed = `${normalizedGrade} ${normalizedField}`.trim();
    if (SUBJECTS_DB[composed]) return SUBJECTS_DB[composed];
  }
  return SUBJECTS_DB['پایه هفتم'];
};

const getGradeType = (grade, field = '') => {
  const config = getGradeConfig(grade, field);
  return config.gradeType || 'middle';
};

const normalizePlannerContext = ({ aiForm, planDetails, timetable }) => {
  const grade = normalizeText(aiForm.grade);
  const field = normalizeText(aiForm.field || '');
  const config = getGradeConfig(grade, field);
  const gradeType = config.gradeType || 'middle';
  const selectedBooks = [...new Set((planDetails.selectedSubjects || []).map(normalizeText).filter(Boolean))].filter(s => config.books.includes(s));
  const studyDays = getStudyDaySet(aiForm.studyDays || 'همه روزها');
  const restDays = new Set();
  for (const d of Array.isArray(planDetails.restDay) ? planDetails.restDay : []) {
    const idx = normalizeDayIndex(d);
    if (idx != null) restDays.add(idx);
  }
  const reviewSessionsPerWeek = planDetails.reviewSessionsPerWeek || {};
  const subjectPriority = planDetails.subjectPriority || {};
  const testsBySubject = planDetails.testsPerSession || {};
  const resolveMentionedSubjects = (text) => {
    const raw = normalizeText(text || '');
    return new Set(selectedBooks.filter(book => raw.includes(normalizeText(book)) || raw.includes(normalizeText(book).replace(/\s*\([^)]*\)/g, ''))));
  };
  const weakSubjectsResolved = resolveMentionedSubjects(aiForm.weaknesses);
  const strongSubjectsResolved = resolveMentionedSubjects(aiForm.strengths);
  const exams = Array.isArray(planDetails.exams) ? planDetails.exams.filter(e => e?.day && e?.book) : [];
  // دریافت مجموعه‌ی تعطیلات پیش‌فرض بر اساس مقطع (پنج‌شنبه و جمعه برای دبستان، فقط جمعه برای سایر مقاطع)
const defaultHolidaySet = getHolidaySet(gradeType);

// دریافت روزهای تعطیل سفارشی از کاربر (به‌صورت آرایه‌ای از نام روزها)
const customHolidays = planDetails.customHolidays || ['پنج‌شنبه', 'جمعه'];

// تبدیل نام روزها به ایندکس (۰=شنبه، ۱=یک‌شنبه، ...)
const customHolidayIndices = customHolidays
  .map(day => normalizeDayIndex(day))
  .filter(idx => idx !== null);

// ترکیب دو مجموعه (پیش‌فرض + سفارشی)
const combinedHolidaySet = new Set([...defaultHolidaySet, ...customHolidayIndices]);
  const context = {
    grade,
    field,
    gradeType,
    goal: normalizeText(aiForm.goal || 'یادگیری درس ها'),
    studyTime: normalizeText(aiForm.studyTime || 'ترکیبی'),
    studyDays,
    restDays,
    selectedBooks,
    wakeUpTime: planDetails.wakeUpTime || '06:30',
    sleepTime: planDetails.sleepTime || '23:00',
    breakfastTime: planDetails.breakfastTime || '07:30',
    lunchTime: planDetails.lunchTime || '13:00',
    dinnerTime: planDetails.dinnerTime || '20:30',
    breakfastDuration: Number(planDetails.breakfastDuration) || DEFAULT_MEAL_DURATIONS.breakfast,
    lunchDuration: Number(planDetails.lunchDuration) || DEFAULT_MEAL_DURATIONS.lunch,
    dinnerDuration: Number(planDetails.dinnerDuration) || DEFAULT_MEAL_DURATIONS.dinner,
    schoolStartTime: planDetails.schoolStartTime || DEFAULT_SCHOOL_START_TIME,
    schoolEndTime: planDetails.schoolEndTime || DEFAULT_SCHOOL_END_TIME,
    afterSchoolCommuteMinutes: Math.max(0, Number(planDetails.afterSchoolCommuteMinutes ?? DEFAULT_AFTER_SCHOOL_COMMUTE)),
    afterSchoolRestStart: planDetails.afterSchoolRestStart || '',
    afterSchoolRestDuration: Math.max(0, Number(planDetails.afterSchoolRestDuration) || 0),
    sessionDuration: planDetails.sessionDuration === 'ترکیبی' ? 'ترکیبی' : Number(planDetails.sessionDuration) || 45,
    breakDuration: Math.max(5, Number(planDetails.breakDuration) || 15),
    catchUpSessions: Math.max(0, Number(planDetails.catchUpSessions) || 0),
    studyHoursSchoolDays: Number(planDetails.studyHoursSchoolDays) || 0,
    studyHoursHolidays: Number(planDetails.studyHoursHolidays) || 0,
    resources: Array.isArray(planDetails.resources) ? planDetails.resources : [],
    reviewSessionsPerWeek,
    subjectPriority,
    testsBySubject,
    subjectDistribution: planDetails.subjectDistribution || {},
    subjectGoal: planDetails.subjectGoal || {},
    extraClasses: Array.isArray(planDetails.extraClasses) ? planDetails.extraClasses : [],
    outsideActivities: Array.isArray(planDetails.outsideActivities) ? planDetails.outsideActivities : [],
    exams,
    examDaysBySubject: normalizeExamMap(exams),
    weakSubjects: weakSubjectsResolved,
    strongSubjects: strongSubjectsResolved,
    strengths: normalizeText(aiForm.strengths || ''),
    isHoliday: (dayIndex) => combinedHolidaySet.has(dayIndex),
    timetable: timetable || {},
    recommendedSleepHours: MIN_RECOMMENDED_SLEEP_HOURS[gradeType] || 8,
  };

  // تنظیم خودکار afterSchoolRestStart بر اساس lunchTime (حذف شده، چون در buildFixedBlocks انجام می‌شود)
  // این بخش کامنت می‌شود تا تداخل نداشته باشد.
  // if (planDetails.afterSchoolRestDuration > 0 && planDetails.lunchTime) { ... }

  return context;
};

const buildEducationalPlannerPrompt = (context, totalWeeklyMinutes) => {
  const selectedSubjectDetails = context.selectedBooks.map(book => {
    const tests = Number(context.testsBySubject?.[book]) || 0;
    const reviews = Number(context.reviewSessionsPerWeek?.[book]) || 0;
    const priority = Number(context.subjectPriority?.[book]) || 2;
    const distribution = context.subjectDistribution?.[book] || 0;
    const goal = context.subjectGoal?.[book] || 'خوب';
    return `- ${book}: اولویت ${priority}/3، تست هر جلسه ${tests}، مرور هفتگی ${reviews}، توزیع ${distribution || 'خودکار'}، هدف ${goal}`;
  }).join('\n');

  const examDetails = context.exams.length
    ? context.exams.map(e => `- ${e.day}: ${e.book}`).join('\n')
    : 'هیچ امتحانی ثبت نشده است.';

  return `
تو «مغز آموزشی» یک سامانه برنامه‌ریز درسی برای دانش‌آموزان ایرانی هستی. تو **فقط برنامه آموزشی/محتوایی** می‌سازی و حق نداری هیچ زمان شروع/پایان، ساعت، تاریخ یا استراحتی تعیین کنی. زمان‌بندی و تمام قیود زمانی بعداً توسط یک موتور قطعی محاسبه می‌شود.

اصل بنیادین: هر چیزی که درباره سرفصل دقیق کتاب مطمئن نیستی را اختراع نکن. شماره فصل/درس/صفحه نساز. در صورت نبود اطمینان، از عبارت‌های امن مانند «بخش مشخص‌شده از کتاب درسی» یا «مباحث اخیر» استفاده کن.

مقطع: ${context.gradeType}
پایه: ${context.grade} ${context.field ? `(${context.field})` : ''}
هدف: ${context.goal}
روزهای مطالعه: ${[...context.studyDays].map(i => DAYS[i]).join('، ')}
مجموع زمان مطالعه هفتگی هدف: ${totalWeeklyMinutes} دقیقه
نقاط ضعف: ${context.weakSubjects.size ? [...context.weakSubjects].join('، ') : 'ثبت نشده'}
نقاط قوت: ${context.strengths || 'ثبت نشده'}
منابع: ${context.resources.join('، ') || 'کتاب درسی'}
حداقل خواب پیشنهادی برای این مقطع: ${context.recommendedSleepHours} ساعت؛ هرگز زمان خواب کاربر را جابه‌جا نکن.

دروس انتخابی:
${selectedSubjectDetails}

امتحانات:
${examDetails}

قواعد آموزشی:
1) در تمام دروس انتخابی پوشش ایجاد کن.
2) دروس با ضعف/امتحان نزدیک/اولویت بالا را زودتر و بیشتر تقویت کن.
3) از یادگیری اولیه، تمرین، بازیابی فعال، مرور فاصله‌دار، آزمون و تحلیل خطا به‌صورت متناسب با هدف استفاده کن.
4) برای دبستان فعالیت عملی/بازی/داستان/تمرین کوتاه بساز و تست را صفر نگه دار.
5) برای متوسطه اول و دوم، دروس تحلیلی را با حل مسئله و دروس حفظی را با بازیابی و مرور ترکیب کن.
6) برای برنامه امتحانی، جلسه‌های پیش‌نیاز، جمع‌بندی و retrieval را در کنار یادگیری اولیه قرار بده.
7) در هیچ خروجی، زمان یا تاریخ تولید نکن.

خروجی فقط JSON معتبر:
{
  "plannerVersion": "${SCHEDULER_VERSION}",
  "tasks": [
    {
      "id": "string-unique",
      "subject": "یکی از دروس انتخابی",
      "topic": "مبحث دقیق فقط در صورت اطمینان؛ در غیر این صورت عبارت امن",
      "activity": "learn|practice|retrieval|review|test|test_analysis",
      "estimatedMinutes": 30,
      "questionCount": 0,
      "priority": 1,
      "repeatable": true,
      "examRelated": false,
      "rationale": "یک دلیل کوتاه"
    }
  ]
}
حداقل برای هر درس انتخابی یک task بساز و مجموع estimatedMinutes را تا حد ممکن به ${totalWeeklyMinutes} دقیقه نزدیک کن. برای پوشش خوب، 2 تا 4 task برای هر درس بساز؛ اما متن را بی‌جهت طولانی نکن.`;
};

const createAbortableFetch = async (url, options, timeoutMs = 45000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

// ============================================================
// نام ماه‌های شمسی به فارسی
// ============================================================
const MONTHS_FA = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
];

// ============================================================
// توابع فرمت‌دهی تاریخ (با moment-jalaali)
// ============================================================
const formatJalaliDate = (momentObj) => {
  if (!momentObj || !momentObj.isValid) return '';
  const day = momentObj.jDate();
  const month = momentObj.jMonth();
  return `${day} ${MONTHS_FA[month]}`;
};

const formatJalaliFullDate = (momentObj) => {
  if (!momentObj || !momentObj.isValid) return '';
  const day = momentObj.jDate();
  const month = momentObj.jMonth();
  const year = momentObj.jYear();
  return `${day} ${MONTHS_FA[month]} ${year}`;
};

const getPersianDayName = (momentObj) => {
  if (!momentObj || !momentObj.isValid) return '';
  const dayIndex = momentObj.day();
  return DAYS[dayIndex];
};

// ============================================================
// توابع کمکی
// ============================================================
const calculateDurationFromTime = (timeStr) => {
  if (!timeStr || !timeStr.includes(' - ')) return 45;
  const [startStr, endStr] = timeStr.split(' - ');
  if (!startStr || !endStr) return 45;
  const [startHour, startMin] = startStr.split(':').map(Number);
  const [endHour, endMin] = endStr.split(':').map(Number);
  if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) return 45;
  const startTotal = startHour * 60 + startMin;
  const endTotal = endHour * 60 + endMin;
  const diff = endTotal - startTotal;
  return diff > 0 ? diff : 45;
};

const formatDuration = (totalMinutes) => {
  if (totalMinutes < 0) return '۰ دقیقه';
  if (totalMinutes === 0) return '۰ دقیقه';
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  let parts = [];
  if (hours > 0) parts.push(`${hours} ساعت`);
  if (mins > 0) parts.push(`${mins} دقیقه`);
  return parts.join(' و ') || '۰ دقیقه';
};

const formatDurationShort = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours === 0 && mins === 0) return '۰';
  if (hours === 0) return `${mins} دقیقه`;
  return `${hours}:${String(mins).padStart(2, '0')} ساعت`;
};

const formatTimerDisplay = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const checkTimeConflictBetween = (time1, time2) => {
  if (!time1 || !time2) return false;
  if (!time1.includes(' - ') || !time2.includes(' - ')) return false;
  const [s1Start, s1End] = time1.split(' - ');
  const [s2Start, s2End] = time2.split(' - ');
  const [h1s, m1s] = s1Start.split(':').map(Number);
  const [h1e, m1e] = s1End.split(':').map(Number);
  const [h2s, m2s] = s2Start.split(':').map(Number);
  const [h2e, m2e] = s2End.split(':').map(Number);
  if (isNaN(h1s) || isNaN(m1s) || isNaN(h1e) || isNaN(m1e) ||
      isNaN(h2s) || isNaN(m2s) || isNaN(h2e) || isNaN(m2e)) return false;
  const start1 = h1s * 60 + m1s;
  const end1 = h1e * 60 + m1e;
  const start2 = h2s * 60 + m2s;
  const end2 = h2e * 60 + m2e;
  return (start1 < end2 && end1 > start2);
};

const checkConflictsInDay = (sessions, excludeId = null) => {
  for (let i = 0; i < sessions.length; i++) {
    for (let j = i + 1; j < sessions.length; j++) {
      if (sessions[i].id === excludeId || sessions[j].id === excludeId) continue;
      if (checkTimeConflictBetween(sessions[i].time, sessions[j].time)) {
        return { conflict: true, session1: sessions[i], session2: sessions[j] };
      }
    }
  }
  return { conflict: false };
};

const extractAndCleanJSON = (text) => {
  let cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
  let start = cleaned.indexOf('{');
  if (start === -1) throw new Error('هیچ JSON معتبری یافت نشد');
  let depth = 0, end = -1, inString = false, escapeNext = false;
  for (let i = start; i < cleaned.length; i++) {
    const char = cleaned[i];
    if (escapeNext) { escapeNext = false; continue; }
    if (char === '\\') { escapeNext = true; continue; }
    if (char === '"' && !escapeNext) { inString = !inString; continue; }
    if (!inString) {
      if (char === '{') depth++;
      else if (char === '}') {
        depth--;
        if (depth === 0) { end = i; break; }
      }
    }
  }
  if (end === -1) throw new Error('پایان JSON پیدا نشد');
  let jsonStr = cleaned.substring(start, end + 1);
  jsonStr = jsonStr.replace(/,\s*}/g, '}').replace(/,\s*\]/g, ']').replace(/,\s*,/g, ',');
  jsonStr = jsonStr.replace(/([{,]\s*)([a-zA-Z\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF][a-zA-Z\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF0-9_\s]*?)\s*:/g, (match, p1, p2) => {
    const trimmed = p2.trim();
    if (!trimmed.startsWith('"') && !trimmed.endsWith('"')) return p1 + '"' + trimmed + '":';
    return match;
  });
  jsonStr = jsonStr.replace(/,\s*}/g, '}').replace(/,\s*\]/g, ']').replace(/\s+/g, ' ').trim();
  return jsonStr;
};

// ============================================================
// کامپوننت اصلی
// ============================================================
export default function LessonPlanning() {
  const { user, aiQuota, consumeAIQuota, refundAIQuota } = useAuth();
  const { coins, addCoins, removeCoins } = useCoins();
  const navigate = useNavigate();

  // ✅ هزینه‌ی ساخت هر برنامه با AI بعد از اتمام سهمیه رایگان
  const AI_PLAN_COST = 20;

  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('weekly');
  const [selectedDay, setSelectedDay] = useState(0);
  const [activeTab, setActiveTab] = useState('study');
  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const [statsView, setStatsView] = useState('week');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [timeModalStartHour, setTimeModalStartHour] = useState(8);
  const [timeModalStartMin, setTimeModalStartMin] = useState(0);
  const [timeModalEndHour, setTimeModalEndHour] = useState(9);
  const [timeModalEndMin, setTimeModalEndMin] = useState(0);
  const [timeModalTarget, setTimeModalTarget] = useState(null);

  const [timerOpen, setTimerOpen] = useState(false);
  const [timerSession, setTimerSession] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const timerIntervalRef = useRef(null);

  const [aiForm, setAiForm] = useState({
    grade: '',
    field: '',
    goal: 'یادگیری درس ها',
    hoursPerDay: '',
    studyDays: 'همه روزها',
    studyTime: 'ترکیبی',
    strengths: '',
    weaknesses: '',
    examDate: '',
    extraActivities: '',
  });

  const [planDetails, setPlanDetails] = useState({
    wakeUpTime: '06:30',
    sleepTime: '23:00',
    breakfastTime: '07:30',
    lunchTime: '13:00',
    dinnerTime: '20:30',
    breakfastDuration: 30,
    lunchDuration: 30,
    dinnerDuration: 30,
    schoolStartTime: '07:00',
    schoolEndTime: '14:00',
    afterSchoolCommuteMinutes: 30,
    afterSchoolRestStart: '',
    afterSchoolRestDuration: 0,
    sessionDuration: 'ترکیبی',
    breakDuration: 15,
    catchUpSessions: 1,
    restDay: [],
    studyHoursSchoolDays: 3,
    studyHoursHolidays: 6,
    resources: ['کتاب درسی'],
    customHolidays: ['پنج‌شنبه', 'جمعه'],
    selectedSubjects: [],
    subjectDistribution: {},
    testsPerSession: {},
    reviewSessionsPerWeek: {},
    subjectPriority: {},
    subjectGoal: {},
    extraClasses: [],
    outsideActivities: [],
    exams: [],
  });

  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editSource, setEditSource] = useState(null);

  const [timetable, setTimetable] = useState(() => {
    const saved = localStorage.getItem('schoolTimetable');
    if (saved) { try { return JSON.parse(saved); } catch { return {}; } }
    return {};
  });

const [theme, setTheme] = useState(() => {
  try {
    return localStorage.getItem('navbar2-drawer-dark') === '1' ? 'dark' : 'light';
  } catch { return 'light'; }
});

// ذخیره در کلید مشترک Navbar2
useEffect(() => {
  try {
    localStorage.setItem('navbar2-drawer-dark', theme === 'dark' ? '1' : '0');
  } catch {}
}, [theme]);

// گوش دادن به تغییرات Navbar2 (storage بین تب‌ها + polling داخل همون تب)
useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      setTheme(e.newValue === '1' ? 'dark' : 'light');
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      const current = localStorage.getItem('navbar2-drawer-dark') === '1' ? 'dark' : 'light';
      setTheme((prev) => (prev !== current ? current : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);

  // بارگذاری برنامه‌ها
  useEffect(() => {
    if (!user?.id) return;
    const key = `curriculum_plans_${user.id}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      let parsed = null;
      try {
        parsed = JSON.parse(saved);
      } catch {
        parsed = null;
      }
      if (Array.isArray(parsed)) {
        const filtered = parsed.filter(p =>
          p && typeof p === 'object' && typeof p.id === 'string' &&
          !p.id.startsWith('sample_') && !p.id.startsWith('test_') && !p.id.startsWith('demo_')
        );
        setPlans(filtered);
        const pinned = filtered.find(p => p.pinned);
        setActivePlanId(pinned ? pinned.id : (filtered[0]?.id || null));
      } else {
        // داده‌ی خراب یا نامعتبر — کلید را حذف نمی‌کنیم، فقط state را خالی می‌کنیم
        setPlans([]);
        setActivePlanId(null);
      }
    } else {
      setPlans([]);
      setActivePlanId(null);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    const key = `curriculum_plans_${user.id}`;
    if (plans.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(plans));
    }
  }, [plans, user]);

  // تایمر
  useEffect(() => {
    if (timerRunning && !timerPaused) {
      timerIntervalRef.current = setInterval(() => {
        setTimerSeconds(prev => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            setTimerRunning(false);
            setTimerOpen(false);
            if (timerSession) {
              markSessionDone(timerSession.planId, timerSession.dayIndex, timerSession.sessionIndex);
              showToast('🎉 زمان مطالعه تمام شد!', 'success');
              setTimerSession(null);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerIntervalRef.current);
    }
    return () => clearInterval(timerIntervalRef.current);
  }, [timerRunning, timerPaused, timerSession]);

  // توابع مدیریت
  const showToast = (message, type = 'info') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
  };

  const createEmptyPlan = (grade = '', field = '', source = 'manual') => {
    const today = moment();
    const startOfWeek = moment(today).startOf('jWeek');
    return {
      id: `${source}_${Date.now()}`,
      title: grade ? `برنامه ${grade}` : 'برنامه جدید',
      pinned: false,
      createdAt: new Date().toISOString(),
      grade,
      field,
      source: source,
      days: DAYS.map((dayName, idx) => {
        const date = moment(startOfWeek).add(idx, 'days');
        return {
          day: dayName,
          date: date.toISOString().split('T')[0],
          sessions: [],
        };
      }),
    };
  };



  const confirmPayAndBuild = () => {
    setShowPayConfirm(false);
    buildPlanWithAI();
  };

  const cancelPayAndBuild = () => {
    setShowPayConfirm(false);
    showToast('پرداخت لغو شد. برنامه‌ای ساخته نشد.', 'info');
  };

  const buildPlanWithAI = useCallback(async () => {
    if (!user) { showToast('لطفاً ابتدا وارد شوید', 'error'); return; }
    if (!aiForm.grade) { showToast('لطفاً پایه تحصیلی خود را انتخاب کنید', 'error'); return; }
    if (!planDetails.selectedSubjects.length) { showToast('لطفاً حداقل یک کتاب درسی را انتخاب کنید', 'error'); return; }
    if (planDetails.studyHoursSchoolDays <= 0 && planDetails.studyHoursHolidays <= 0) {
      showToast('لطفاً ساعات مطالعه را در روزهای مدرسه و تعطیل وارد کنید', 'error'); return;
    }

    // ✅ چک نهایی اعتبار: یا سهمیه رایگان دارد، یا سکه کافی
    const canUseFree = aiQuota.remaining > 0;
    const canPayCoins = coins >= AI_PLAN_COST;
    if (!canUseFree && !canPayCoins) {
      showToast(
        `سهمیه رایگان شما تمام شده و برای ساخت بعدی ${AI_PLAN_COST} سکه لازم است. موجودی فعلی: ${coins} سکه`,
        'error'
      );
      return;
    }

    setIsLoading(true);
    let charged = null;   // ✅ برای پیگیری شارژ و بازگشت در صورت خطا
    try {
      const context = normalizePlannerContext({ aiForm, planDetails, timetable });
      if (!context.selectedBooks.length) {
        throw new Error('هیچ درس معتبری از پایه انتخاب‌شده باقی نمانده است.');
      }

      const preflight = preflightValidateCapacity(context);
      if (!preflight.valid) {
        const first = preflight.issues[0];
        throw new Error(`زمان آزاد ${first.day} کافی نیست: هدف ${first.target} دقیقه، ظرفیت زمان‌بندی‌شونده ${first.capacity} دقیقه و زمان خالی خام ${first.rawFreeMinutes ?? first.capacity} دقیقه است. زمان مدرسه، خواب، وعده‌ها، رفت‌وآمد یا کلاس‌های ثبت‌شده را بررسی کنید.`);
      }

      // ✅✅✅ شارژ هزینه — درست قبل از تماس با AI، بعد از تمام preflight ها
      // این ترتیب تضمین می‌کند که اگر ورودی‌ها اشتباه باشند، هزینه‌ای کسر نشود
      if (canUseFree) {
        const ok = consumeAIQuota();
        if (!ok) throw new Error('کاهش سهمیه رایگان ناموفق بود. لطفاً دوباره تلاش کنید.');
        charged = { type: 'free' };
      } else {
        // ⚠️ نکته‌ی حیاتی: قبل از removeCoins، از کافی بودن سکه مطمئن شو
        // چون removeCoins در CoinContext به صفر می‌چسباند ولی همان عدد را return می‌کند
        if (coins < AI_PLAN_COST) {
          throw new Error(`سکه کافی نیست. نیاز: ${AI_PLAN_COST}، موجودی: ${coins}`);
        }
        const removed = removeCoins(AI_PLAN_COST);
        if (removed !== AI_PLAN_COST) {
          // احتمالاً cooldown فعال بوده (کاربر خیلی سریع کلیک کرده)
          throw new Error(`کسر ${AI_PLAN_COST} سکه ناموفق بود. لطفاً چند لحظه بعد دوباره تلاش کنید.`);
        }
        charged = { type: 'coins', amount: AI_PLAN_COST };
      }

      const studyDays = [...context.studyDays].filter(d => buildDailyTargetMinutes(context, d) > 0);
      const weeklyMinutes = studyDays.reduce((sum, d) => sum + buildDailyTargetMinutes(context, d), 0);

      let educationalTasks = [];
      const plannerPrompt = buildEducationalPlannerPrompt(context, weeklyMinutes);
      const headers = { 'Content-Type': 'application/json' };
      

      try {
        const response = await createAbortableFetch(CONFIG.baseURL, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            model: CONFIG.modelText,
            messages: [
              { role: 'system', content: 'تو فقط Educational Planner هستی؛ زمان‌بندی نکن و فقط JSON آموزشی برگردان.' },
              { role: 'user', content: plannerPrompt },
            ],
            temperature: 0.2,
            max_tokens: 6000,
            response_format: { type: 'json_object' },
          }),
        }, CONFIG.requestTimeoutMs);
        if (!response.ok) throw new Error(`AI HTTP ${response.status}`);
        const data = await response.json();
        const content = data?.choices?.[0]?.message?.content;
        const parsed = extractAndCleanJSON(content || '{}');
        const aiPlan = JSON.parse(parsed);
        educationalTasks = normalizeEducationalTasks(aiPlan.tasks, context);
      } catch (aiError) {
        console.warn('Educational planner unavailable; using deterministic safe fallback.', aiError);
        educationalTasks = normalizeEducationalTasks(buildCautiousFallbackTasks(context), context);
      }

      if (!educationalTasks.length) educationalTasks = normalizeEducationalTasks(buildCautiousFallbackTasks(context), context);

      educationalTasks = educationalTasks.map(t => ({
        ...t,
        subject: context.selectedBooks.includes(t.subject) ? t.subject : context.selectedBooks[0],
        topic: safeTopic(t.topic, t.subject, t.activity),
        questionCount: context.gradeType === 'elementary' ? 0 : t.questionCount,
      }));

      const weeklyTasks = expandTasksToWeeklyNeed(educationalTasks, context, weeklyMinutes);
      const totalTaskMinutes = weeklyTasks.reduce((sum, t) => sum + (t.remainingMinutes || 0), 0);
      if (totalTaskMinutes < weeklyMinutes) {
        throw new Error('موتور آموزشی نتوانست به اندازه کافی وظیفه آموزشی معتبر برای هفته تولید کند.');
      }

      const days = [];
      const recentSubjectDays = new Set();
      const schedulerState = { learnedLastDay: new Map(), lastAnyDay: new Map(), lastReviewDay: new Map() };
      for (let d = 0; d < 7; d++) {
        const fixedBlocks = preflight.capacityByDay[d].fixed;
        const target = buildDailyTargetMinutes(context, d);
        const dayResult = planDaySessions(d, context, weeklyTasks, fixedBlocks, target, recentSubjectDays, schedulerState);
        if (dayResult.remainingTarget > 960) {
          throw new Error(`موتور زمان‌بندی در ${DAYS[d]} نتوانست ${dayResult.remainingTarget} دقیقه باقیمانده را در زمان آزاد معتبر قرار دهد.`);
        }
        days.push({
          day: DAYS[d],
          date: moment(moment().startOf('jWeek')).add(d, 'days').toISOString().split('T')[0],
          sessions: dayResult.sessions,
        });
      }

      if (context.catchUpSessions > 0) {
        const eligibleDays = studyDays.slice().sort((a,b) => b-a);
        let remainingCatchUp = context.catchUpSessions;
        for (const d of eligibleDays) {
          if (remainingCatchUp <= 0) break;
          const sessions = days[d].sessions;
          for (let i = sessions.length - 1; i >= 0 && remainingCatchUp > 0; i--) {
            const s = sessions[i];
            if (s.sessionType === 'catch_up') continue;
            s.sessionType = 'catch_up';
            s.topic = `جبران و تثبیت: ${s.topic}`;
            s.learningMethod = 'catch_up_and_consolidation';
            remainingCatchUp -= 1;
          }
        }
      }

      const candidate = {
        title: `برنامه ${context.grade}${context.field ? ` ${context.field}` : ''} - ویژه ${context.goal}`,
        plannerVersion: SCHEDULER_VERSION,
        source: 'ai',
        generatedBy: 'AI-Educational-Planner + Deterministic-Scheduler',
        days,
      };

      let validation = validatePlanHardConstraints(candidate, context);
      if (!validation.valid) {
        const repaired = repairPlan(candidate, context);
        validation = validatePlanHardConstraints(repaired, context);
        if (validation.valid) Object.assign(candidate, repaired);
      }

      if (!validation.valid) {
        console.error('FINAL PLAN VALIDATION FAILED', validation.errors);
        throw new Error(`برنامه تولیدشده از فیلتر نهایی عبور نکرد: ${validation.errors.slice(0, 3).join(' | ')}`);
      }

      const newPlan = {
        id: `ai_${Date.now()}`,
        title: candidate.title,
        pinned: false,
        createdAt: new Date().toISOString(),
        grade: context.grade,
        field: context.field,
        source: 'ai',
        plannerVersion: SCHEDULER_VERSION,
        days: candidate.days.map((day) => ({
          day: day.day,
          date: day.date,
          sessions: (day.sessions || []).map((session, sIdx) => ({
            id: `ai_${Date.now()}_${day.day}_${sIdx}_${Math.random().toString(36).slice(2,7)}`,
            book: session.book,
            time: session.time,
            topic: session.topic,
            testCount: session.testCount || 0,
            done: false,
            duration: session.duration,
            sessionType: session.sessionType,
            learningMethod: session.learningMethod,
            priority: session.priority,
            reason: session.reason || '',
          })),
        })),
      };

      setPlans(prev => [...prev, newPlan]);
      setActivePlanId(newPlan.id);
      setIsAIModalOpen(false);
      setEditingPlanId(newPlan.id);
      setEditForm(JSON.parse(JSON.stringify(newPlan)));
      setIsEditMode(true);
      setEditSource('ai');
      setActiveTab('ai');
      showToast('🎉 برنامه با اعتبارسنجی کامل ساخته شد!', 'success');
    } catch (error) {
      // ✅✅✅ بازگشت هزینه فقط در صورت شکست واقعی
      // (اگر برنامه ساخته شده باشد، catch اجرا نمی‌شود و هزینه باقی می‌ماند)
      if (charged) {
        if (charged.type === 'free') {
          refundAIQuota();
        } else if (charged.type === 'coins') {
          addCoins(charged.amount);
        }
      }
      console.error('AI Build Error:', error);
      showToast(`برنامه معتبر ساخته نشد: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [user, aiForm, planDetails, plans, timetable]);

  // مدیریت برنامه‌ها
  const getActivePlan = useMemo(() => {
    if (!plans || plans.length === 0) return null;
    return plans.find(p => p.id === activePlanId) || plans[0] || null;
  }, [plans, activePlanId]);

  const pinPlan = (id) => {
    setPlans(prev => prev.map(p => ({ ...p, pinned: p.id === id })));
    setActivePlanId(id);
    showToast('📌 برنامه پین شد', 'success');
  };

  const deletePlan = (id) => {
    setDeleteTargetId(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    const id = deleteTargetId;
    if (!id) return;
    const newPlans = plans.filter(p => p.id !== id);
    if (activePlanId === id) {
      const pinned = newPlans.find(p => p.pinned);
      setActivePlanId(pinned ? pinned.id : (newPlans[0]?.id || null));
    }
    setPlans(newPlans);
    if (editingPlanId === id) {
      setEditingPlanId(null);
      setEditForm(null);
      setIsEditMode(false);
      setEditSource(null);
    }
    setDeleteModalOpen(false);
    setDeleteTargetId(null);
    showToast('🗑 برنامه حذف شد', 'info');
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setDeleteTargetId(null);
  };

  const duplicatePlan = (id) => {
    const plan = plans.find(p => p.id === id);
    if (!plan) return;
    const newPlan = {
      ...plan,
      id: `copy_${Date.now()}`,
      title: `${plan.title} (کپی)`,
      pinned: false,
      days: plan.days.map(day => ({
        ...day,
        sessions: day.sessions.map(s => ({ ...s, id: `copy_${Date.now()}_${Math.random()}`, done: false })),
      })),
    };
    setPlans(prev => [...prev, newPlan]);
    setActivePlanId(newPlan.id);
    showToast('📋 برنامه کپی شد', 'success');
  };

  const markSessionDone = (planId, dayIndex, sessionIndex) => {
    setPlans(prev => prev.map(p => {
      if (p.id !== planId) return p;
      const newDays = [...p.days];
      if (newDays[dayIndex]?.sessions?.[sessionIndex]) {
        newDays[dayIndex].sessions[sessionIndex].done = true;
      }
      return { ...p, days: newDays };
    }));
  };

  const toggleSessionDone = (planId, dayIndex, sessionIndex) => {
    setPlans(prev => prev.map(p => {
      if (p.id !== planId) return p;
      const newDays = [...p.days];
      if (newDays[dayIndex]?.sessions?.[sessionIndex]) {
        newDays[dayIndex].sessions[sessionIndex].done = !newDays[dayIndex].sessions[sessionIndex].done;
      }
      return { ...p, days: newDays };
    }));
  };

  const startEditingPlan = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;
    setEditingPlanId(planId);
    setEditForm(JSON.parse(JSON.stringify(plan)));
    setIsEditMode(true);
    setEditSource(plan.source);
    if (plan.source === 'ai') {
      setActiveTab('ai');
    } else {
      setActiveTab('manual');
    }
  };

  const saveEditPlan = () => {
    if (!editForm) return;

    for (let dIdx = 0; dIdx < editForm.days.length; dIdx++) {
      const day = editForm.days[dIdx];
      const sessions = day.sessions || [];
      const result = checkConflictsInDay(sessions);
      if (result.conflict) {
        setConflictData({
          day: day.day,
          session1: result.session1,
          session2: result.session2,
          dayIndex: dIdx,
        });
        setConflictModalOpen(true);
        return;
      }
    }

    setPlans(prev => prev.map(p => p.id === editForm.id ? editForm : p));
    if (activePlanId === editForm.id) {
      setActivePlanId(editForm.id);
    }
    setIsEditMode(false);
    setEditingPlanId(null);
    setEditForm(null);
    setEditSource(null);
    showToast('💾 تغییرات ذخیره شد', 'success');
  };

  const cancelEditPlan = () => {
    setIsEditMode(false);
    setEditingPlanId(null);
    setEditForm(null);
    setEditSource(null);
    setConflictModalOpen(false);
    setConflictData(null);
  };

  const updateEditSession = (dayIdx, sessionIdx, field, value) => {
    if (!editForm) return;
    const newDays = [...editForm.days];
    const session = newDays[dayIdx].sessions[sessionIdx];
    session[field] = value;
    if (field === 'time') {
      session.duration = calculateDurationFromTime(value);
    }
    setEditForm({ ...editForm, days: newDays });
  };

  const addEditSession = (dayIdx) => {
    if (!editForm) return;
    const newDays = [...editForm.days];
    newDays[dayIdx].sessions.push({
      id: `edit_${Date.now()}`,
      book: 'درس جدید',
      time: '۰۸:۰۰ - ۰۸:۴۵',
      topic: 'مبحث جدید',
      testCount: 0,
      done: false,
      duration: 45,
    });
    setEditForm({ ...editForm, days: newDays });
  };

  const removeEditSession = (dayIdx, sessionIdx) => {
    if (!editForm) return;
    const newDays = [...editForm.days];
    newDays[dayIdx].sessions.splice(sessionIdx, 1);
    setEditForm({ ...editForm, days: newDays });
  };



    // 🆕 باز کردن مودال زمان برای فیلدهای AI
  const openTimeModalForAI = (currentValue, onSave) => {
    const parts = String(currentValue || '08:00').split(':');
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    setTimeModalStartHour(isNaN(h) ? 8 : Math.min(23, Math.max(0, h)));
    setTimeModalStartMin(isNaN(m) ? 0 : Math.min(59, Math.max(0, m)));
    setTimeModalTarget({ onSave, single: true });
    setTimeModalOpen(true);
  };


  const openTimeModalForEdit = (dayIdx, sessionIdx) => {
    if (!editForm) return;
    const session = editForm.days[dayIdx].sessions[sessionIdx];
    const [startStr, endStr] = session.time.split(' - ');
    if (startStr && endStr) {
      const [sh, sm] = startStr.split(':').map(Number);
      const [eh, em] = endStr.split(':').map(Number);
      if (!isNaN(sh) && !isNaN(sm)) { setTimeModalStartHour(sh); setTimeModalStartMin(sm); }
      if (!isNaN(eh) && !isNaN(em)) { setTimeModalEndHour(eh); setTimeModalEndMin(em); }
    }
    setTimeModalTarget({ dayIdx, sessionIdx });
    setTimeModalOpen(true);
  };

  const closeTimeModal = () => {
    setTimeModalOpen(false);
    setTimeModalTarget(null);
  };

  const saveTimeModal = () => {
    if (!timeModalTarget) return;
    const startStr = `${String(timeModalStartHour).padStart(2, '0')}:${String(timeModalStartMin).padStart(2, '0')}`;

    if (timeModalTarget.single) {
      if (typeof timeModalTarget.onSave === 'function') {
        timeModalTarget.onSave(startStr);
      }
    } else {
      if (!editForm) return;
      const { dayIdx, sessionIdx } = timeModalTarget;
      const endStr = `${String(timeModalEndHour).padStart(2, '0')}:${String(timeModalEndMin).padStart(2, '0')}`;
      updateEditSession(dayIdx, sessionIdx, 'time', `${startStr} - ${endStr}`);
    }

    setTimeModalOpen(false);
    setTimeModalTarget(null);
  };

  const incrementHour = (type) => {
    if (type === 'start') setTimeModalStartHour(prev => (prev + 1) % 24);
    else setTimeModalEndHour(prev => (prev + 1) % 24);
  };
  const decrementHour = (type) => {
    if (type === 'start') setTimeModalStartHour(prev => (prev - 1 + 24) % 24);
    else setTimeModalEndHour(prev => (prev - 1 + 24) % 24);
  };
  const incrementMinute = (type) => {
    if (type === 'start') setTimeModalStartMin(prev => (prev + 5) % 60);
    else setTimeModalEndMin(prev => (prev + 5) % 60);
  };
  const decrementMinute = (type) => {
    if (type === 'start') setTimeModalStartMin(prev => (prev - 5 + 60) % 60);
    else setTimeModalEndMin(prev => (prev - 5 + 60) % 60);
  };
  const handleStartHourInput = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val >= 0 && val <= 23) setTimeModalStartHour(val);
  };
  const handleStartMinInput = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val >= 0 && val <= 59) setTimeModalStartMin(val);
  };
  const handleEndHourInput = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val >= 0 && val <= 23) setTimeModalEndHour(val);
  };
  const handleEndMinInput = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val >= 0 && val <= 59) setTimeModalEndMin(val);
  };

  const openTimer = (planId, dayIndex, sessionIndex) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;
    const session = plan.days[dayIndex]?.sessions?.[sessionIndex];
    if (!session) return;
    const duration = calculateDurationFromTime(session.time);
    setTimerSession({ planId, dayIndex, sessionIndex });
    setTimerSeconds(duration * 60);
    setTimerRunning(false);
    setTimerPaused(false);
    setTimerOpen(true);
  };

  const closeTimer = () => {
    setTimerOpen(false);
    setTimerRunning(false);
    setTimerPaused(false);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    setTimerSession(null);
  };

  const startTimer = () => { if (timerSeconds <= 0) return; setTimerRunning(true); setTimerPaused(false); };
  const pauseTimer = () => { setTimerRunning(false); setTimerPaused(true); };
  const resumeTimer = () => { setTimerRunning(true); setTimerPaused(false); };
  const resetTimer = () => {
    setTimerRunning(false);
    setTimerPaused(false);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    const session = timerSession ? plans.find(p => p.id === timerSession.planId)?.days?.[timerSession.dayIndex]?.sessions?.[timerSession.sessionIndex] : null;
    if (session) {
      const duration = calculateDurationFromTime(session.time);
      setTimerSeconds(duration * 60);
    } else {
      setTimerSeconds(45 * 60);
    }
  };

  const createNewManualPlan = () => {
    const newPlan = createEmptyPlan(user?.grade || '', user?.field || '', 'manual');
    setPlans(prev => [...prev, newPlan]);
    setActivePlanId(newPlan.id);
    startEditingPlan(newPlan.id);
  };

  const renderNoPlanState = () => (
    <div className="empty-state">
      <span className="icon"><FiCalendar /></span>
      <h3>برنامه درسی نداری؟</h3>
      <p>با هوش مصنوعی یا دستی، یک برنامه اختصاصی برای خودت بساز.</p>
      <div className="empty-actions">
        <button className="btn-cta primary" onClick={() => { setActiveTab('ai'); setIsAIModalOpen(true); }}>
          <FaRobot /> ساخت با AI
        </button>
        <button className="btn-cta secondary" onClick={() => setActiveTab('manual')}>
          <FiEdit /> ساخت دستی
        </button>
      </div>
    </div>
  );

  const renderDeleteModal = () => {
    if (!deleteModalOpen) return null;
    return (
      <div className="modal-overlay" onClick={cancelDelete}>
        <div className="modal-box delete-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-icon"><FiAlertCircle /></div>
          <h3>حذف برنامه</h3>
          <p>آیا از حذف این برنامه مطمئن هستید؟ این اقدام قابل بازگشت نیست.</p>
          <div className="modal-actions">
            <button className="btn-modal-cancel" onClick={cancelDelete}>انصراف</button>
            <button className="btn-modal-delete" onClick={confirmDelete}>حذف</button>
          </div>
        </div>
      </div>
    );
  };

  const renderConflictModal = () => {
    if (!conflictModalOpen || !conflictData) return null;
    return (
      <div className="modal-overlay" onClick={resolveConflict}>
        <div className="modal-box conflict-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-icon"><FiAlertCircle style={{ color: '#ef4444' }} /></div>
          <h3>⚠️ تداخل زمانی!</h3>
          <p>در روز <strong>{conflictData.day}</strong>، دو جلسه با زمان‌های زیر تداخل دارند:</p>
          <div className="conflict-sessions">
            <div className="conflict-item">
              <span className="conflict-title">{conflictData.session1.book}</span>
              <span className="conflict-time">{conflictData.session1.time}</span>
            </div>
            <div className="conflict-arrow">⟷</div>
            <div className="conflict-item">
              <span className="conflict-title">{conflictData.session2.book}</span>
              <span className="conflict-time">{conflictData.session2.time}</span>
            </div>
          </div>
          <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            لطفاً زمان یکی از جلسات را تغییر دهید و دوباره ذخیره کنید.
          </p>
          <div className="modal-actions">
            <button className="btn-modal-ok" onClick={resolveConflict}>متوجه شدم</button>
          </div>
        </div>
      </div>
    );
  };

  const resolveConflict = () => {
    setConflictModalOpen(false);
    setConflictData(null);
  };

  const renderTimeModal = () => {
    if (!timeModalOpen) return null;
    const isSingle = !!(timeModalTarget && timeModalTarget.single);
    return (
      <div className="modal-overlay" onClick={closeTimeModal}>
        <div className="modal-box time-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>⏱ {isSingle ? 'انتخاب ساعت' : 'انتخاب بازه‌ی زمانی'}</h3>
            <button className="close-btn" onClick={closeTimeModal}><FiX /></button>
          </div>
          <div className="time-modal-body">
            <div className="time-picker-group">
<div className="time-picker">
  <span className="time-label">{isSingle ? 'ساعت' : 'شروع'}</span>
  <div className="time-controls">
    <button className="time-arrow" onClick={() => incrementMinute('start')}><FiUp /></button>
    <input type="number" value={timeModalStartMin} onChange={handleStartMinInput} min="0" max="59" className="time-input" />
    <button className="time-arrow" onClick={() => decrementMinute('start')}><FiDown /></button>
  </div>
  <span className="time-colon">:</span>
  <div className="time-controls">
    <button className="time-arrow" onClick={() => incrementHour('start')}><FiUp /></button>
    <input type="number" value={timeModalStartHour} onChange={handleStartHourInput} min="0" max="23" className="time-input" />
    <button className="time-arrow" onClick={() => decrementHour('start')}><FiDown /></button>
  </div>
</div>
              {!isSingle && (
                <>
                  <span className="time-separator">تا</span>
<div className="time-picker">
  <span className="time-label">پایان</span>
  <div className="time-controls">
    <button className="time-arrow" onClick={() => incrementMinute('end')}><FiUp /></button>
    <input type="number" value={timeModalEndMin} onChange={handleEndMinInput} min="0" max="59" className="time-input" />
    <button className="time-arrow" onClick={() => decrementMinute('end')}><FiDown /></button>
  </div>
  <span className="time-colon">:</span>
  <div className="time-controls">
    <button className="time-arrow" onClick={() => incrementHour('end')}><FiUp /></button>
    <input type="number" value={timeModalEndHour} onChange={handleEndHourInput} min="0" max="23" className="time-input" />
    <button className="time-arrow" onClick={() => decrementHour('end')}><FiDown /></button>
  </div>
</div>
                </>
              )}
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-modal-cancel" onClick={closeTimeModal}>انصراف</button>
            <button className="btn-modal-save" onClick={saveTimeModal}>ذخیره</button>
          </div>
        </div>
      </div>
    );
  };



















  const renderPlanDays = useCallback(() => {
    const plan = getActivePlan;
    if (!plan) return renderNoPlanState();
    if (plan.days.every(d => d.sessions.length === 0)) return renderNoPlanState();

    const days = plan.days || [];
    const displayedDays = viewMode === 'daily' ? [days[selectedDay] || days[0]] : days;

    return displayedDays.map((day, idx) => {
      const realIdx = viewMode === 'daily' ? selectedDay : idx;
      const dayData = days[realIdx];
      if (!dayData) return null;
      const doneCount = dayData.sessions?.filter(s => s.done).length || 0;
      const totalCount = dayData.sessions?.length || 0;

      return (
        <motion.div
          key={realIdx}
          className="day-plan-card glass"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.05 }}
        >
          <div className="day-header">
            <div className="day-name">
              <span className="day-title">{dayData.day}</span>
              <span className="day-date">
                {dayData.date ? formatJalaliDate(moment(dayData.date)) : ''}
              </span>
            </div>
            <div className="day-progress">
              <span>{doneCount}/{totalCount}</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: totalCount > 0 ? `${(doneCount / totalCount) * 100}%` : 0 }} />
              </div>
            </div>
          </div>

          <div className="sessions-list">
            {dayData.sessions?.map((session, sIdx) => (
              <motion.div
                key={session.id || sIdx}
                className={`session-item ${session.done ? 'done' : ''}`}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="session-time">{session.time}</div>
                <div className="session-info">
                  <div className="session-title">{session.book}</div>
                  <div className="session-detail">{session.topic}{session.testCount > 0 ? ` - ${session.testCount} تست` : ''}</div>
                  <div className="session-duration">⏱ {formatDuration(session.duration || calculateDurationFromTime(session.time))}</div>
                </div>
                <div className="session-actions">
                  {!session.done && (
                    <>
                      <button
                        className="btn-start-study"
                        onClick={(e) => { e.stopPropagation(); openTimer(plan.id, realIdx, sIdx); }}
                        title="شروع مطالعه"
                      >
                        <FiPlay /> شروع
                      </button>
                      <button
                        className="btn-done-study"
                        onClick={(e) => { e.stopPropagation(); toggleSessionDone(plan.id, realIdx, sIdx); }}
                        title="انجام شد"
                      >
                        <FiCheckCircle /> انجام شد
                      </button>
                    </>
                  )}
                  {session.done && (
                    <span className="done-label">✅ انجام شده</span>
                  )}
                </div>
                {plan.source === 'manual' && (
                  <div className="session-source-badge">✋ دستی</div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      );
    });
  }, [getActivePlan, viewMode, selectedDay, plans]);

  const renderPrintView = () => {
    const plan = getActivePlan;
    if (!plan) return <div className="empty-state">برنامه‌ای برای چاپ وجود ندارد</div>;

    return (
      <div className="print-view" id="printArea">
        <div className="print-header">
          <h2>{plan.title}</h2>
          <p>پایه: {plan.grade || 'نامشخص'}</p>
          <p>منبع: {plan.source === 'ai' ? '🤖 هوش مصنوعی' : '✋ دستی'}</p>
          <p>تاریخ: {formatJalaliFullDate(moment())}</p>
        </div>
        <table className="print-table">
          <thead>
            <tr>
              <th>روز</th>
              <th>ساعت</th>
              <th>کتاب</th>
              <th>مبحث / تست</th>
              <th>مدت</th>
              <th>وضعیت</th>
            </tr>
          </thead>
          <tbody>
            {plan.days.map((day, dIdx) => (
              day.sessions.map((session, sIdx) => (
                <tr key={`${dIdx}-${sIdx}`}>
                  <td>{day.day}</td>
                  <td>{session.time}</td>
                  <td>{session.book}</td>
                  <td>{session.topic}{session.testCount > 0 ? ` - ${session.testCount} تست` : ''}</td>
                  <td>{formatDuration(session.duration || calculateDurationFromTime(session.time))}</td>
                  <td>{session.done ? '✅ انجام شده' : '⬜ انجام نشده'}</td>
                </tr>
              ))
            ))}
          </tbody>
        </table>
        <div className="print-footer">
          <p>تعداد جلسات: {plan.days.reduce((acc, d) => acc + d.sessions.length, 0)}</p>
          <p>تعداد انجام شده: {plan.days.reduce((acc, d) => acc + d.sessions.filter(s => s.done).length, 0)}</p>
        </div>
      </div>
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const renderPlanCarousel = () => {
    if (plans.length === 0) return null;
    const currentIndex = plans.findIndex(p => p.id === activePlanId);
    const total = plans.length;

    return (
      <div className="plan-carousel glass">
        <button
          className="carousel-btn prev"
          onClick={() => {
            const newIndex = (currentIndex - 1 + total) % total;
            setActivePlanId(plans[newIndex].id);
          }}
          disabled={total <= 1}
        >
          <FiChevronLeft />
        </button>
        <div className="carousel-indicators">
          {plans.map((p, idx) => (
            <span
              key={p.id}
              className={`dot ${p.id === activePlanId ? 'active' : ''}`}
              onClick={() => setActivePlanId(p.id)}
            />
          ))}
        </div>
        <button
          className="carousel-btn next"
          onClick={() => {
            const newIndex = (currentIndex + 1) % total;
            setActivePlanId(plans[newIndex].id);
          }}
          disabled={total <= 1}
        >
          <FiChevronRight />
        </button>
        <div className="carousel-actions">
          <button
            className="carousel-action pin"
            onClick={() => pinPlan(activePlanId)}
            title="پین کردن"
          >
            <FiBookmark />
          </button>
          <button
            className="carousel-action delete"
            onClick={() => deletePlan(activePlanId)}
            title="حذف"
          >
            <FiTrash2 />
          </button>
          <button
            className="carousel-action edit"
            onClick={() => startEditingPlan(activePlanId)}
            title="ویرایش"
          >
            <FiEdit />
          </button>
        </div>
      </div>
    );
  };

  const renderTabs = () => {
    const tabs = [
      { id: 'study', icon: <FiBookOpen />, label: 'مطالعه' },
      { id: 'ai', icon: <FaRobot />, label: 'ساخت با AI' },
      { id: 'manual', icon: <FiEdit />, label: 'ساخت دستی' },
      { id: 'school', icon: <FaSchool />, label: 'برنامه مدرسه' },
      { id: 'stats', icon: <FaChartLine />, label: 'آمار' },
    ];
    return (
      <div className="tab-nav">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? 'active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'study': return renderStudyTab();
      case 'ai': return renderAITab();
      case 'manual': return renderManualTab();
      case 'school': return renderSchoolTab();
      case 'stats': return renderStatsTab();
      default: return null;
    }
  };

  const renderStudyTab = () => (
    <div className="tab-content study-tab">
      {(!getActivePlan || getActivePlan.days.every(d => d.sessions.length === 0)) ? renderNoPlanState() : (
        <>
          <div className="study-toolbar">
            <button
              className={`view-toggle-btn ${viewMode === 'weekly' ? 'active' : ''}`}
              onClick={() => setViewMode('weekly')}
            >
              <FiGrid /> هفتگی
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'daily' ? 'active' : ''}`}
              onClick={() => setViewMode('daily')}
            >
              <FiList /> روزانه
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'print' ? 'active' : ''}`}
              onClick={() => setViewMode('print')}
            >
              <FiPrinter /> چاپ
            </button>
          </div>
          {viewMode === 'print' ? (
            <>
              {renderPrintView()}
              <button className="btn-print" onClick={handlePrint}>
                <FiPrinter /> چاپ برنامه
              </button>
            </>
          ) : (
            <>
              {renderPlanCarousel()}
              <div className="plan-sheet card glass">
                <div className="plan-header">
                  <h3>{getActivePlan?.title}</h3>
                  <div className="plan-header-badges">
                    {getActivePlan?.source === 'ai' && <span className="badge-ai">🤖 AI</span>}
                    {getActivePlan?.source === 'manual' && <span className="badge-manual">✋ دستی</span>}
                  </div>
                  <span className="plan-dates">
                    {getActivePlan?.days.length > 0 &&
                      `${formatJalaliDate(moment(getActivePlan.days[0].date))} - ${formatJalaliDate(moment(getActivePlan.days[getActivePlan.days.length - 1].date))}`}
                  </span>
                </div>
                <div className="plan-content">{renderPlanDays()}</div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  const renderAITab = () => {
    const isEditing = isEditMode && editSource === 'ai';
    const availableBooksForEdit = editForm ? getGradeConfig(editForm.grade, editForm.field).books || [] : [];

    return (
      <div className="tab-content ai-tab">
        <div className="ai-showcase glass">
          <div className="ai-icon"><FaBrain /></div>
          <h4>✨ هوش مصنوعی برنامه‌ساز</h4>
          <p>برنامه‌ای منحصربه‌فرد متناسب با مقطع، اهداف و سبک مطالعه‌ات بساز.</p>
          <div className="sample-plans">
            <span className="sample-tag">📘 دبستان</span>
            <span className="sample-tag">📗 متوسطه اول</span>
            <span className="sample-tag">📕 متوسطه دوم</span>
          </div>
        </div>

        {!isEditing ? (
          <>
            <div className="ai-form card glass">
              <button className="btn-generate" onClick={() => setIsAIModalOpen(true)}>
                <FaRobot /> ساخت با AI
              </button>
            </div>
            {plans.filter(p => p.source === 'ai').length > 0 && (
              <div className="ai-existing-plans card glass">
                <h4>برنامه‌های ساخته شده با AI</h4>
                {plans.filter(p => p.source === 'ai').map(p => (
                  <div key={p.id} className="ai-plan-item">
                    <span>{p.title}</span>
                    <button onClick={() => startEditingPlan(p.id)}><FiEdit /> ویرایش</button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="edit-form-container card glass">
            <div className="edit-header">
              <h4>✏️ ویرایش برنامه: {editForm.title}</h4>
              <div className="edit-header-actions">
                <button
                  className="edit-header-btn delete"
                  onClick={() => deletePlan(editForm.id)}
                  title="حذف برنامه"
                >
                  <FiTrash2 /> حذف
                </button>
                <button
                  className="edit-header-btn save"
                  onClick={saveEditPlan}
                  title="ذخیره تغییرات"
                >
                  <FiSave /> ذخیره
                </button>
                <button
                  className="edit-header-btn cancel"
                  onClick={cancelEditPlan}
                  title="انصراف"
                >
                  <FiX /> انصراف
                </button>
              </div>
            </div>

            <div className="edit-form-group">
              <label>عنوان برنامه</label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>

            {editForm.days.map((day, dIdx) => (
              <div key={dIdx} className="edit-day-group">
                <h5>{day.day}</h5>
                {day.sessions.map((session, sIdx) => (
                  <div key={sIdx} className="edit-session-row">
                    <select
                      value={session.book}
                      onChange={(e) => updateEditSession(dIdx, sIdx, 'book', e.target.value)}
                      className="edit-input-book"
                    >
                      {availableBooksForEdit.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                    <button
                      className="edit-time-btn"
                      onClick={() => openTimeModalForEdit(dIdx, sIdx)}
                    >
                      <FiClock /> {session.time}
                    </button>
                    <input
                      type="text"
                      value={session.topic}
                      onChange={(e) => updateEditSession(dIdx, sIdx, 'topic', e.target.value)}
                      placeholder="مبحث"
                      className="edit-input-topic"
                    />
                    <input
                      type="number"
                      value={session.testCount}
                      onChange={(e) => updateEditSession(dIdx, sIdx, 'testCount', parseInt(e.target.value) || 0)}
                      placeholder="تعداد تست"
                      className="edit-input-testcount"
                      min="0"
                    />
                    <span className="edit-duration-display">
                      {formatDuration(session.duration || calculateDurationFromTime(session.time))}
                    </span>
                    <button className="remove-session-btn" onClick={() => removeEditSession(dIdx, sIdx)}>
                      <FiX />
                    </button>
                  </div>
                ))}
                <button className="add-session-btn" onClick={() => addEditSession(dIdx)}>
                  <FiPlus /> افزودن جلسه
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderManualTab = () => {
    const isEditing = isEditMode && editSource === 'manual';
    const availableBooksForEdit = editForm ? getGradeConfig(editForm.grade, editForm.field).books || [] : [];

    if (isEditing) {
      return (
        <div className="tab-content manual-tab">
          <div className="edit-form-container card glass">
            <div className="edit-header">
              <h4>✏️ ویرایش برنامه دستی: {editForm.title}</h4>
              <div className="edit-header-actions">
                <button
                  className="edit-header-btn delete"
                  onClick={() => deletePlan(editForm.id)}
                  title="حذف برنامه"
                >
                  <FiTrash2 /> حذف
                </button>
                <button
                  className="edit-header-btn save"
                  onClick={saveEditPlan}
                  title="ذخیره تغییرات"
                >
                  <FiSave /> ذخیره
                </button>
                <button
                  className="edit-header-btn cancel"
                  onClick={cancelEditPlan}
                  title="انصراف"
                >
                  <FiX /> انصراف
                </button>
              </div>
            </div>

            <div className="edit-form-group">
              <label>عنوان برنامه</label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>

            {editForm.days.map((day, dIdx) => (
              <div key={dIdx} className="edit-day-group">
                <h5>{day.day}</h5>
                {day.sessions.map((session, sIdx) => (
                  <div key={sIdx} className="edit-session-row">
                    <select
                      value={session.book}
                      onChange={(e) => updateEditSession(dIdx, sIdx, 'book', e.target.value)}
                      className="edit-input-book"
                    >
                      {availableBooksForEdit.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                    <button
                      className="edit-time-btn"
                      onClick={() => openTimeModalForEdit(dIdx, sIdx)}
                    >
                      <FiClock /> {session.time}
                    </button>
                    <input
                      type="text"
                      value={session.topic}
                      onChange={(e) => updateEditSession(dIdx, sIdx, 'topic', e.target.value)}
                      placeholder="مبحث"
                      className="edit-input-topic"
                    />
                    <input
                      type="number"
                      value={session.testCount}
                      onChange={(e) => updateEditSession(dIdx, sIdx, 'testCount', parseInt(e.target.value) || 0)}
                      placeholder="تعداد تست"
                      className="edit-input-testcount"
                      min="0"
                    />
                    <span className="edit-duration-display">
                      {formatDuration(session.duration || calculateDurationFromTime(session.time))}
                    </span>
                    <button className="remove-session-btn" onClick={() => removeEditSession(dIdx, sIdx)}>
                      <FiX />
                    </button>
                  </div>
                ))}
                <button className="add-session-btn" onClick={() => addEditSession(dIdx)}>
                  <FiPlus /> افزودن جلسه
                </button>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="tab-content manual-tab">
        <div className="manual-main">
          <button className="btn-create-new-plan card glass" onClick={createNewManualPlan}>
            <FiPlusCircle size={32} />
            <span>ساخت برنامه درسی جدید</span>
          </button>

          <div className="manual-existing-plans card glass">
            <h4>📋 برنامه‌های دستی شما</h4>
            {plans.filter(p => p.source === 'manual').length === 0 ? (
              <p className="empty-manual">هنوز برنامه دستی ساخته نشده است.</p>
            ) : (
              plans.filter(p => p.source === 'manual').map(p => (
                <div key={p.id} className="manual-plan-item">
                  <span>{p.title}</span>
                  <div className="manual-plan-actions">
                    <button onClick={() => startEditingPlan(p.id)}><FiEdit /> ویرایش</button>
                    <button onClick={() => deletePlan(p.id)}><FiTrash2 /> حذف</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSchoolTab = () => {
    const days = ['شنبه', 'یک‌شنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه'];
    const periods = ['زنگ اول', 'زنگ دوم', 'زنگ سوم', 'زنگ چهارم', 'زنگ پنجم', 'زنگ ششم'];

    const updateTimetable = (dayIdx, period, value) => {
      const key = `tt_${dayIdx}_${period}`;
      const newData = { ...timetable, [key]: value };
      setTimetable(newData);
      localStorage.setItem('schoolTimetable', JSON.stringify(newData));
    };

    return (
      <div className="tab-content school-tab">
        <div className="school-header">
          <h3><FaSchool /> برنامه کلاس‌های هفتگی</h3>
        </div>
        <div className="tt-grid card glass">
          <div className="cell header">ساعت</div>
          {days.map(d => <div key={d} className="cell header">{d}</div>)}
          {periods.map(p => (
            <React.Fragment key={p}>
              <div className="cell header">{p}</div>
              {days.map((d, idx) => (
                <div key={`${idx}-${p}`} className="cell">
                  <input
                    type="text"
                    placeholder="..."
                    value={timetable[`tt_${idx}_${p}`] || ''}
                    onChange={(e) => updateTimetable(idx, p, e.target.value)}
                  />
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
        <p className="school-note"><FiFileText /> تغییرات به‌طور خودکار ذخیره می‌شوند</p>
      </div>
    );
  };

  const renderStatsTab = () => {
    if (!plans || plans.length === 0) {
      return (
        <div className="tab-content stats-tab">
          <div className="empty-state">
            <FaChartLine className="empty-icon" />
            <h3>هنوز آماری وجود ندارد</h3>
            <p>یک برنامه بسازید و مطالعه را شروع کنید تا آمار شما نمایش داده شود.</p>
            <button className="btn-cta primary" onClick={() => { setActiveTab('ai'); setIsAIModalOpen(true); }}>
              <FaRobot /> ساخت برنامه
            </button>
          </div>
        </div>
      );
    }

    const todayJalali = moment();
    const todayStr = todayJalali.toISOString().split('T')[0];
    const weekStartJalali = moment(todayJalali).startOf('jWeek');
    const weekEndJalali = moment(todayJalali).endOf('jWeek');
    const weekStartStr = weekStartJalali.toISOString().split('T')[0];
    const weekEndStr = weekEndJalali.toISOString().split('T')[0];
    const monthStartJalali = moment(todayJalali).startOf('jMonth');
    const monthEndJalali = moment(todayJalali).endOf('jMonth');
    const monthStartStr = monthStartJalali.toISOString().split('T')[0];
    const monthEndStr = monthEndJalali.toISOString().split('T')[0];

    const allSessions = [];
    plans.forEach(plan => {
      plan.days.forEach(day => {
        day.sessions.forEach(session => {
          if (session.done) {
            const duration = session.duration || calculateDurationFromTime(session.time);
            allSessions.push({
              ...session,
              date: day.date,
              planTitle: plan.title,
              durationMinutes: duration,
            });
          }
        });
      });
    });

    const todaySessions = allSessions.filter(s => s.date === todayStr);
    const weekSessions = allSessions.filter(s => s.date >= weekStartStr && s.date <= weekEndStr);
    const monthSessions = allSessions.filter(s => s.date >= monthStartStr && s.date <= monthEndStr);

    const sumMinutes = (sessions) => {
      return sessions.reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
    };

    const todayMinutes = sumMinutes(todaySessions);
    const weekMinutes = sumMinutes(weekSessions);
    const monthMinutes = sumMinutes(monthSessions);

    const formatTime = (totalMinutes) => {
      if (totalMinutes === 0) return '۰ دقیقه';
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      let parts = [];
      if (hours > 0) parts.push(`${hours} ساعت`);
      if (mins > 0) parts.push(`${mins} دقیقه`);
      return parts.join(' و ') || '۰ دقیقه';
    };

    const formatTimeShort = (totalMinutes) => {
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      if (hours === 0 && mins === 0) return '۰';
      if (hours === 0) return `${mins}`;
      return `${hours}:${String(mins).padStart(2, '0')}`;
    };

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const d = moment(weekStartJalali).add(i, 'days');
      const dateStr = d.toISOString().split('T')[0];
      const dayName = getPersianDayName(d);
      const dayMinutes = allSessions
        .filter(s => s.date === dateStr)
        .reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
      weekDates.push({
        day: dayName,
        value: Math.round((dayMinutes / 60) * 100) / 100
      });
    }

    const monthDates = [];
    const daysInMonth = monthEndJalali.jDate();
    for (let i = 1; i <= daysInMonth; i++) {
      const d = moment(monthStartJalali).add(i - 1, 'days');
      const dateStr = d.toISOString().split('T')[0];
      const dayMinutes = allSessions
        .filter(s => s.date === dateStr)
        .reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
      monthDates.push({
        date: dateStr,
        value: Math.round((dayMinutes / 60) * 100) / 100,
        display: formatJalaliDate(d),
      });
    }

    const lastWeekStart = moment(todayJalali).subtract(1, 'weeks').startOf('jWeek');
    const lastWeekEnd = moment(todayJalali).subtract(1, 'weeks').endOf('jWeek');
    const lastWeekStartStr = lastWeekStart.toISOString().split('T')[0];
    const lastWeekEndStr = lastWeekEnd.toISOString().split('T')[0];
    const lastWeekSessions = allSessions.filter(s => s.date >= lastWeekStartStr && s.date <= lastWeekEndStr);
    const lastWeekMinutes = sumMinutes(lastWeekSessions);

    const daysPassed = todayJalali.diff(weekStartJalali, 'days') + 1;
    const relevantDays = weekDates.slice(0, Math.min(daysPassed, 7));
    const avgDaily = relevantDays.length > 0
      ? Math.round((relevantDays.reduce((sum, d) => sum + d.value, 0) / relevantDays.length) * 100) / 100
      : 0;
    const bestDay = relevantDays.length > 0
      ? Math.max(...relevantDays.map(d => d.value))
      : 0;

    const chartData = statsView === 'week' ? weekDates : monthDates;
    const hasData = chartData.some(d => d.value > 0);

    const customTooltipStyle = {
      contentStyle: {
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '14px',
        border: '1px solid rgba(226,232,240,0.2)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
        padding: '12px 18px',
        backdropFilter: 'blur(8px)',
      },
      labelStyle: {
        fontWeight: 700,
        color: '#1e293b',
        fontSize: '14px',
      },
      itemStyle: {
        fontSize: '13px',
        fontWeight: 600,
      },
    };

    return (
      <div className="tab-content stats-tab">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="num">{formatTimeShort(todayMinutes)}</div>
            <div className="lbl">{formatTime(todayMinutes)} امروز</div>
          </div>
          <div className="stat-card green">
            <div className="num">{formatTimeShort(weekMinutes)}</div>
            <div className="lbl">{formatTime(weekMinutes)} این هفته</div>
          </div>
          <div className="stat-card orange">
            <div className="num">{formatTimeShort(monthMinutes)}</div>
            <div className="lbl">{formatTime(monthMinutes)} این ماه</div>
          </div>
        </div>

        <div className="stats-filter">
          <button
            className={`filter-btn ${statsView === 'week' ? 'active' : ''}`}
            onClick={() => setStatsView('week')}
          >
            هفته
          </button>
          <button
            className={`filter-btn ${statsView === 'month' ? 'active' : ''}`}
            onClick={() => setStatsView('month')}
          >
            ماه
          </button>
        </div>

        <div className="stats-chart-wrap card glass">
          <div className="chart-label">
            {statsView === 'week' ? '📈 عملکرد هفتگی (ساعت)' : '📊 عملکرد ماهانه (ساعت)'}
          </div>
          {!hasData ? (
            <div className="chart-empty-state">
              <span>📭</span>
              <p>هنوز داده‌ای برای نمایش وجود ندارد</p>
              <small>جلسات مطالعه‌ات را کامل کن تا آمارت اینجا نمایش داده شود.</small>
            </div>
          ) : (
            <>
              <div style={{ width: '100%', height: '180px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  {statsView === 'week' ? (
                    <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(226,232,240,0.12)" vertical={false} />
                      <XAxis
                        dataKey="day"
                        tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 600 }}
                        axisLine={{ stroke: 'rgba(226,232,240,0.2)' }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                        axisLine={{ stroke: 'rgba(226,232,240,0.2)' }}
                        tickLine={false}
                        width={30}
                        domain={[0, 'auto']}
                      />
                      <Tooltip
                        contentStyle={customTooltipStyle.contentStyle}
                        labelStyle={customTooltipStyle.labelStyle}
                        formatter={(value) => {
                          const totalMinutes = Math.round(value * 60);
                          const h = Math.floor(totalMinutes / 60);
                          const m = totalMinutes % 60;
                          return [`${h}:${String(m).padStart(2, '0')}`, 'زمان مطالعه'];
                        }}
                        cursor={{ fill: 'rgba(124,58,237,0.04)' }}
                      />
                      <Bar
                        dataKey="value"
                        fill="#7c3aed"
                        radius={[6, 6, 0, 0]}
                        barSize={32}
                        animationDuration={1200}
                        animationEasing="ease-in-out"
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.value > 0 ? `hsl(${260 + index * 10}, 80%, 60%)` : 'rgba(226,232,240,0.2)'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  ) : (
                    <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(226,232,240,0.12)" vertical={false} />
                      <XAxis
                        dataKey="display"
                        tick={{ fill: '#94a3b8', fontSize: 9, fontWeight: 600 }}
                        axisLine={{ stroke: 'rgba(226,232,240,0.2)' }}
                        tickLine={false}
                        interval={Math.floor(chartData.length / 7)}
                      />
                      <YAxis
                        tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                        axisLine={{ stroke: 'rgba(226,232,240,0.2)' }}
                        tickLine={false}
                        width={30}
                        domain={[0, 'auto']}
                      />
                      <Tooltip
                        contentStyle={customTooltipStyle.contentStyle}
                        labelStyle={customTooltipStyle.labelStyle}
                        formatter={(value) => {
                          const totalMinutes = Math.round(value * 60);
                          const h = Math.floor(totalMinutes / 60);
                          const m = totalMinutes % 60;
                          return [`${h}:${String(m).padStart(2, '0')}`, 'زمان مطالعه'];
                        }}
                        labelFormatter={(label) => `📅 ${label}`}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        fill="url(#areaGrad)"
                        name="زمان مطالعه"
                        dot={{ fill: '#3b82f6', r: 3, strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 5, fill: '#3b82f6', stroke: '#60a5fa', strokeWidth: 2 }}
                        animationDuration={1500}
                        animationEasing="ease-in-out"
                      />
                      <defs>
                        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25" />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
                        </linearGradient>
                      </defs>
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              </div>
              <div className="chart-hint">هر {statsView === 'week' ? 'میله' : 'نقطه'} = یک روز</div>
            </>
          )}
        </div>

        <div className="stats-comparison card glass">
          <div className="cmp-item">
            <div className="num">{formatTimeShort(lastWeekMinutes)}</div>
            <div className="lbl">هفته قبل</div>
            <div className="change up">
              {lastWeekMinutes > 0 ? `${Math.round(((weekMinutes - lastWeekMinutes) / (lastWeekMinutes || 1)) * 100)}%` : '۰٪'}
            </div>
          </div>
          <div className="cmp-item">
            <div className="num">{formatTimeShort(Math.round(avgDaily * 60))}</div>
            <div className="lbl">میانگین روزانه (ساعت)</div>
          </div>
          <div className="cmp-item">
            <div className="num">{formatTimeShort(Math.round(bestDay * 60))}</div>
            <div className="lbl">بهترین روز (ساعت)</div>
          </div>
        </div>
      </div>
    );
  };

  const renderSidebar = () => (
    <AnimatePresence>
      {isSidebarOpen && (
        <>
          <motion.div
            className="sidebar-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSidebarOpen(false)}
          />
          <motion.aside
            className="plans-sidebar"
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', damping: 25 }}
          >
            <div className="sidebar-header">
              <h3>📋 برنامه‌های من</h3>
              <button className="close-btn" onClick={() => setIsSidebarOpen(false)}><FiX /></button>
            </div>
            <div className="sidebar-body">
              {plans.length === 0 && <p className="empty-manual">هنوز برنامه‌ای نساخته‌اید</p>}
              {plans.map(plan => (
                <div
                  key={plan.id}
                  className={`plan-list-item ${activePlanId === plan.id ? 'active' : ''}`}
                  onClick={() => { setActivePlanId(plan.id); setIsSidebarOpen(false); }}
                >
                  <div className="plan-list-info">
                    <span className="plan-list-title">{plan.title}</span>
                    <span className="plan-list-grade">{plan.grade || 'بدون پایه'}</span>
                    <span className="plan-list-source">
                      {plan.source === 'ai' ? '🤖' : '✋'}
                    </span>
                  </div>
                  <div className="plan-list-actions">
                    {plan.pinned && <FiStar className="pinned-icon" />}
                    <button onClick={(e) => { e.stopPropagation(); pinPlan(plan.id); }} className="pin-btn" title="پین کردن"><FiBookmark /></button>
                    <button onClick={(e) => { e.stopPropagation(); duplicatePlan(plan.id); }} className="duplicate-btn" title="کپی کردن"><FiPlus /></button>
                    <button onClick={(e) => { e.stopPropagation(); deletePlan(plan.id); }} className="delete-btn" title="حذف"><FiTrash2 /></button>
                    <button onClick={(e) => { e.stopPropagation(); startEditingPlan(plan.id); }} className="edit-btn" title="ویرایش"><FiEdit /></button>
                  </div>
                </div>
              ))}
              <button className="new-plan-btn" onClick={() => { setIsAIModalOpen(true); setIsSidebarOpen(false); }}><FiPlus /> ساخت برنامه جدید</button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );

  const renderAIModal = () => {
    const grades = Object.keys(SUBJECTS_DB);
    const fields = ['ریاضی', 'تجربی', 'انسانی'];

    if (!isAIModalOpen) return null;

    return (
      <div className="ai-modal-overlay" onClick={() => !isLoading && setIsAIModalOpen(false)}>
        <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2><FaBrain /> ساخت برنامه با هوش مصنوعی</h2>
            <button className="close-btn" onClick={() => !isLoading && setIsAIModalOpen(false)}><FiX /></button>
          </div>
          <div className="modal-body">
            <p className="modal-desc">اطلاعات خود را وارد کنید تا یک برنامه درسی کاملاً شخصی‌سازی‌شده برای شما ساخته شود.</p>
            <div className="form-group">
              <label>پایه تحصیلی <span className="required">*</span></label>
              <select value={aiForm.grade} onChange={(e) => setAiForm(prev => ({ ...prev, grade: e.target.value }))} disabled={isLoading}>
                <option value="">انتخاب پایه...</option>
                {grades.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                📊 چه کتاب هایی در برنامه ریزی باشند؟
              </h4>
              {aiForm.grade && (
                <div className="subject-selection-card">
                  {getGradeConfig(aiForm.grade, aiForm.field).books.map(book => {
                    const isSelected = planDetails.selectedSubjects.includes(book);
                    return (
                      <div key={book} style={{
                        padding: '10px 12px',
                        marginBottom: '8px',
                        background: isSelected ? 'rgba(167,139,250,0.04)' : 'rgba(226,232,240,0.04)',
                        borderRadius: '12px',
                        border: isSelected ? '1px solid rgba(167,139,250,0.12)' : '1px solid rgba(226,232,240,0.08)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: isSelected ? '8px' : '0' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPlanDetails(prev => ({
                                  ...prev,
                                  selectedSubjects: [...prev.selectedSubjects, book],
                                  subjectDistribution: { ...prev.subjectDistribution, [book]: 0 },
                                  testsPerSession: { ...prev.testsPerSession, [book]: 10 },
                                  reviewSessionsPerWeek: { ...prev.reviewSessionsPerWeek, [book]: 1 },
                                  subjectPriority: { ...prev.subjectPriority, [book]: 2 },
                                  subjectGoal: { ...prev.subjectGoal, [book]: 'خوب' },
                                }));
                              } else {
                                setPlanDetails(prev => {
                                  const newSelected = prev.selectedSubjects.filter(s => s !== book);
                                  const { [book]: _, ...newDistribution } = prev.subjectDistribution;
                                  const { [book]: __, ...newTests } = prev.testsPerSession;
                                  const { [book]: ___, ...newReviews } = prev.reviewSessionsPerWeek;
                                  const { [book]: ____, ...newPriority } = prev.subjectPriority;
                                  const { [book]: _____, ...newGoal } = prev.subjectGoal;
                                  return {
                                    ...prev,
                                    selectedSubjects: newSelected,
                                    subjectDistribution: newDistribution,
                                    testsPerSession: newTests,
                                    reviewSessionsPerWeek: newReviews,
                                    subjectPriority: newPriority,
                                    subjectGoal: newGoal,
                                  };
                                });
                              }
                            }}
                          />
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>{book}</span>
                          {isSelected && <span style={{ fontSize: '11px', color: '#a78bfa' }}>✅ انتخاب شده</span>}
                        </div>

                        {isSelected && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', paddingTop: '6px' }}>
                            <div>
                              <label style={{ fontSize: '11px', color: '#94a3b8', display: 'block' }}>تعداد تست در هر جلسه</label>
                              <input
                                type="number"
                                min="0"
                                max="50"
                                value={planDetails.testsPerSession[book] ?? 10}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  setPlanDetails(prev => ({
                                    ...prev,
                                    testsPerSession: { ...prev.testsPerSession, [book]: Math.min(50, Math.max(0, val)) }
                                  }));
                                }}
                                style={{ width: '100%', padding: '4px 6px', fontSize: '12px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '6px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', color: '#94a3b8', display: 'block' }}>مرور در هفته</label>
                              <select
                                value={planDetails.reviewSessionsPerWeek[book] ?? 1}
                                onChange={(e) => setPlanDetails(prev => ({
                                  ...prev,
                                  reviewSessionsPerWeek: { ...prev.reviewSessionsPerWeek, [book]: parseInt(e.target.value) }
                                }))}
                                style={{ width: '100%', padding: '4px 6px', fontSize: '12px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '6px' }}
                              >
                                <option value="0">۰</option>
                                <option value="1">۱</option>
                                <option value="2">۲</option>
                                <option value="3">۳</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', color: '#94a3b8', display: 'block' }}>ضریب اهمیت</label>
                              <select
                                value={planDetails.subjectPriority[book] || 2}
                                onChange={(e) => setPlanDetails(prev => ({
                                  ...prev,
                                  subjectPriority: { ...prev.subjectPriority, [book]: parseInt(e.target.value) }
                                }))}
                                style={{ width: '100%', padding: '4px 6px', fontSize: '12px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '6px' }}
                              >
                                <option value="1">۱ (کم)</option>
                                <option value="2">۲ (متوسط)</option>
                                <option value="3">۳ (زیاد)</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/*<div className="form-group">
              <label>برنامه هفتگی مدرسه</label>
              <div style={{
                padding: '12px 16px',
                background: Object.values(timetable).some(v => v && v.trim() !== '')
                  ? 'rgba(52, 211, 153, 0.04)'
                  : 'rgba(226, 232, 240, 0.04)',
                borderRadius: '12px',
                border: Object.values(timetable).some(v => v && v.trim() !== '')
                  ? '1px solid rgba(52, 211, 153, 0.12)'
                  : '1px solid rgba(226, 232, 240, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                justifyContent: 'space-between'
              }}>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>
                    {Object.values(timetable).some(v => v && v.trim() !== '') ? '✅ برنامه مدرسه ثبت شده است' : '⚠️ برنامه مدرسه ثبت نشده است'}
                  </span>
                  <br />
                  <small style={{ color: '#94a3b8', fontSize: '12px' }}>
                    {Object.values(timetable).some(v => v && v.trim() !== '')
                      ? 'برنامه شما در تب "برنامه مدرسه" ذخیره شده و در برنامه‌ریزی استفاده می‌شود.'
                      : 'لطفاً در تب "برنامه مدرسه" برنامه خود را وارد کنید تا هوش مصنوعی از آن استفاده کند.'}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-outline btn-sm"
                  onClick={() => {
                    setActiveTab('school');
                    setIsAIModalOpen(false);
                  }}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {Object.values(timetable).some(v => v && v.trim() !== '') ? '✏️ ویرایش' : '➕ ثبت برنامه'}
                </button>
              </div>
            </div>*/}

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
  <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
    🏫 روزهای تعطیل مدرسه در این هفته
  </h4>
  <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
    روزهایی که به مدرسه نمی‌روید را انتخاب کنید. در این روزها بلوک مدرسه اعمال نمی‌شود.
  </p>
  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
    {DAYS.map(day => (
      <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={planDetails.customHolidays.includes(day)}
          onChange={(e) => {
            if (e.target.checked) {
              setPlanDetails(prev => ({
                ...prev,
                customHolidays: [...prev.customHolidays, day]
              }));
            } else {
              setPlanDetails(prev => ({
                ...prev,
                customHolidays: prev.customHolidays.filter(d => d !== day)
              }));
            }
          }}
          disabled={isLoading}
        />
        {day}
      </label>
    ))}
  </div>
  <small style={{ color: '#94a3b800', fontSize: '11px' }}>
    در حالت پیش‌فرض، پنج‌شنبه و جمعه به‌عنوان روزهای تعطیل در نظر گرفته می‌شوند. با انتخاب روزهای اضافی، آن‌ها نیز به لیست تعطیلات اضافه می‌شوند.
  </small>
</div>

            <div className="form-group">
              <label>هدف اصلی از مطالعه</label>
              <select value={aiForm.goal} onChange={(e) => setAiForm(prev => ({ ...prev, goal: e.target.value }))} disabled={isLoading}>
                <option value="یادگیری درس ها">یادگیری درس ها</option>
                <option value="پیش‌خوانی" >پیش‌خوانی</option>
                <option value="آمادگی امتحان تستی">آمادگی امتحان تستی</option>
                <option value="آمادگی امتحان تشریحی">آمادگی امتحان تشریحی</option>
                <option value="آمادگی کنکور">آمادگی کنکور</option>
                <option value="مرور درس ها">مرور درس ها</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group half">
                <label>روزهای مطالعه</label>
                <select value={aiForm.studyDays} onChange={(e) => setAiForm(prev => ({ ...prev, studyDays: e.target.value }))} disabled={isLoading}>
                  <option value="همه روزها" selected>همه روزها (شامل جمعه)</option>
                  <option value="شنبه تا پنجشنبه">شنبه تا پنجشنبه</option>
                  <option value="شنبه تا چهارشنبه">شنبه تا چهارشنبه</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>زمان مطالعه (صبح/عصر/شب)</label>
              <select value={aiForm.studyTime} onChange={(e) => setAiForm(prev => ({ ...prev, studyTime: e.target.value }))} disabled={isLoading}>
                <option value="ترکیبی" selected>ترکیبی (صبح و شب)</option>
                <option value="صبح">فقط صبح</option>
                <option value="عصر">فقط عصر</option>
                <option value="شب">فقط شب</option>
              </select>
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                ⏰ زمان‌های ثابت روزانه
              </h4>
              <div className="form-row">
                <div className="form-group half">
                  <label>ساعت بیدار شدن</label>
                  <input type="text" readOnly value={planDetails.wakeUpTime || ''} placeholder="--:--" onClick={() => openTimeModalForAI(planDetails.wakeUpTime, (v) => setPlanDetails(prev => ({ ...prev, wakeUpTime: v })))} style={{ cursor: 'pointer', direction: 'ltr', textAlign: 'center' }} />
                </div>
                <div className="form-group half">
                  <label>ساعت خواب</label>
                  <input type="text" readOnly value={planDetails.sleepTime || ''} placeholder="--:--" onClick={() => openTimeModalForAI(planDetails.sleepTime, (v) => setPlanDetails(prev => ({ ...prev, sleepTime: v })))} style={{ cursor: 'pointer', direction: 'ltr', textAlign: 'center' }} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group half">
                  <label>ساعت صبحانه</label>
                  <input type="text" readOnly value={planDetails.breakfastTime || ''} placeholder="--:--" onClick={() => openTimeModalForAI(planDetails.breakfastTime, (v) => setPlanDetails(prev => ({ ...prev, breakfastTime: v })))} style={{ cursor: 'pointer', direction: 'ltr', textAlign: 'center' }} />
                </div>
                <div className="form-group half">
                  <label>ساعت ناهار</label>
                  <input type="text" readOnly value={planDetails.lunchTime || ''} placeholder="--:--" onClick={() => openTimeModalForAI(planDetails.lunchTime, (v) => setPlanDetails(prev => ({ ...prev, lunchTime: v })))} style={{ cursor: 'pointer', direction: 'ltr', textAlign: 'center' }} />
                </div>
              </div>
              <div className="form-group">
                <label>ساعت شام</label>
                <input type="text" readOnly value={planDetails.dinnerTime || ''} placeholder="--:--" onClick={() => openTimeModalForAI(planDetails.dinnerTime, (v) => setPlanDetails(prev => ({ ...prev, dinnerTime: v })))} style={{ cursor: 'pointer', direction: 'ltr', textAlign: 'center' }} />
              </div>
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                🏫 زمان مدرسه و مسیر بازگشت
              </h4>
              <div className="form-row">
                <div className="form-group half">
                  <label>شروع مدرسه</label>
                  <input type="text" readOnly value={planDetails.schoolStartTime || ''} placeholder="--:--" onClick={() => openTimeModalForAI(planDetails.schoolStartTime, (v) => setPlanDetails(prev => ({ ...prev, schoolStartTime: v })))} style={{ cursor: 'pointer', direction: 'ltr', textAlign: 'center' }} />
                </div>
                <div className="form-group half">
                  <label>پایان مدرسه</label>
                  <input type="text" readOnly value={planDetails.schoolEndTime || ''} placeholder="--:--" onClick={() => openTimeModalForAI(planDetails.schoolEndTime, (v) => setPlanDetails(prev => ({ ...prev, schoolEndTime: v })))} style={{ cursor: 'pointer', direction: 'ltr', textAlign: 'center' }} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group half">
                  <label>مدت رفت‌وآمد بعد از مدرسه (دقیقه)</label>
                  <select value={planDetails.afterSchoolCommuteMinutes} onChange={(e) => setPlanDetails(prev => ({ ...prev, afterSchoolCommuteMinutes: parseInt(e.target.value) || 0 }))}>
                    <option value="0">۰</option>
                    <option value="15">۱۵</option>
                    <option value="30">۳۰</option>
                    <option value="45">۴۵</option>
                    <option value="60">۶۰</option>
                    <option value="90">۹۰</option>
                  </select>
                </div>
                <div className="form-group half">
                  <label>مدت استراحت/خواب نیمروزی</label>
                  <select value={planDetails.afterSchoolRestDuration} onChange={(e) => setPlanDetails(prev => ({ ...prev, afterSchoolRestDuration: parseInt(e.target.value) || 0 }))}>
                    <option value="0">بدون استراحت ثبت‌شده</option>
                    <option value="30">۳۰ دقیقه</option>
                    <option value="45">۴۵ دقیقه</option>
                    <option value="60">۱ ساعت</option>
                    <option value="90">۱:۳۰ ساعت</option>
                  </select>
                </div>
              </div>
              {/*{planDetails.afterSchoolRestDuration > 0 && (
                <div className="form-group">
                  <label>شروع استراحت/خواب نیمروزی (به‌صورت خودکار محاسبه می‌شود)</label>
                  <input type="time" value={planDetails.afterSchoolRestStart} disabled style={{ opacity: 0.6 }} />
                  <small style={{ color: '#94a3b8', fontSize: '11px' }}>این زمان بر اساس ساعت ناهار و مدت آن به‌طور خودکار تنظیم می‌شود.</small>
                </div>
              )}
              <small style={{ color: '#94a3b8', fontSize: '11px' }}>
                اگر برنامه مدرسه را وارد کرده باشی، از پایان آخرین زنگ هم استفاده می‌شود؛ ولی «پایان مدرسه» برای جلوگیری از آزاد اعلام شدن زمان‌های بین زنگ‌ها معیار اصلی ظرفیت است. در روزهای تعطیل این بلوک مدرسه اعمال نمی‌شود.
              </small>*/}
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                🍽️ مدت وعده‌های غذایی
              </h4>
              <div className="form-row">
                <div className="form-group half">
                  <label>مدت صبحانه</label>
                  <select value={planDetails.breakfastDuration} onChange={(e) => setPlanDetails(prev => ({ ...prev, breakfastDuration: parseInt(e.target.value) || 30 }))}>
                    <option value="15">۱۵ دقیقه</option><option value="30">۳۰ دقیقه</option><option value="45">۴۵ دقیقه</option><option value="60">۶۰ دقیقه</option>
                  </select>
                </div>
                <div className="form-group half">
                  <label>مدت ناهار</label>
                  <select value={planDetails.lunchDuration} onChange={(e) => setPlanDetails(prev => ({ ...prev, lunchDuration: parseInt(e.target.value) || 30 }))}>
                    <option value="15">۱۵ دقیقه</option><option value="30">۳۰ دقیقه</option><option value="45">۴۵ دقیقه</option><option value="60">۶۰ دقیقه</option><option value="90">۹۰ دقیقه</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>مدت شام</label>
                <select value={planDetails.dinnerDuration} onChange={(e) => setPlanDetails(prev => ({ ...prev, dinnerDuration: parseInt(e.target.value) || 30 }))}>
                  <option value="15">۱۵ دقیقه</option><option value="30">۳۰ دقیقه</option><option value="45">۴۵ دقیقه</option><option value="60">۶۰ دقیقه</option><option value="90">۹۰ دقیقه</option>
                </select>
              </div>
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                ⏱ تنظیمات جلسات مطالعه
              </h4>
              <div className="form-row">
                <div className="form-group half">
                  <label>مدت هر جلسه مطالعه (دقیقه)</label>
                  <select value={planDetails.sessionDuration} onChange={(e) => setPlanDetails(prev => ({ ...prev, sessionDuration: e.target.value }))}>
                    <option value="ترکیبی">ترکیبی</option>
                    <option value="15">۱۵ دقیقه</option>
                    <option value="30">۳۰ دقیقه</option>
                    <option value="45">۴۵ دقیقه</option>
                    <option value="60">1 ساعت</option>
                    <option value="90">1:30 ساعت</option>
                    <option value="120">2 ساعت</option>
                  </select>
                </div>
                <div className="form-group half">
                  <label>استراحت بین جلسات (دقیقه)</label>
                  <select value={planDetails.breakDuration} onChange={(e) => setPlanDetails(prev => ({ ...prev, breakDuration: parseInt(e.target.value) }))}>
                    <option value="10" >۱۰ دقیقه</option>
                    <option value="15">۱۵ دقیقه</option>
                    <option value="20" >۲۰ دقیقه</option>
                    <option value="25">25 دقیقه</option>
                    <option value="30">30 دقیقه</option>
                    <option value="45">45 دقیقه</option>
                    <option value="60">60 دقیقه (1 ساعت)</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group half">
                  <label>جلسات جبرانی در هفته</label>
                  <select value={planDetails.catchUpSessions} onChange={(e) => setPlanDetails(prev => ({ ...prev, catchUpSessions: parseInt(e.target.value) }))}>
                    <option value="0">بدون جلسه جبرانی</option>
                    <option value="1" >۱ جلسه</option>
                    <option value="2">۲ جلسه</option>
                    <option value="3">۳ جلسه</option>
                    <option value="4">4 جلسه</option>
                    <option value="5">5 جلسه</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                📚 ساعات مطالعه در روزهای مختلف
              </h4>
              <div className="form-row">
                <div className="form-group half">
                  <label>ساعت مطالعه در روزهای مدرسه</label>
                  <input type="number" min="1" max="12" step="0.5" value={planDetails.studyHoursSchoolDays} onChange={(e) => setPlanDetails(prev => ({ ...prev, studyHoursSchoolDays: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div className="form-group half">
                  <label>ساعت مطالعه در روزهای تعطیل</label>
                  <input type="number" min="1" max="12" step="0.5" value={planDetails.studyHoursHolidays} onChange={(e) => setPlanDetails(prev => ({ ...prev, studyHoursHolidays: parseFloat(e.target.value) || 0 }))} />
                </div>
              </div>
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                📖 دسترسی به منابع کمک‌آموزشی
              </h4>
              <div className="form-group">
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {["کتاب درسی",'کتاب کار', 'جزوه'].map(item => (
                    <label key={item} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={planDetails.resources.includes(item)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPlanDetails(prev => ({ ...prev, resources: [...prev.resources, item] }));
                          } else {
                            setPlanDetails(prev => ({ ...prev, resources: prev.resources.filter(r => r !== item) }));
                          }
                        }}
                      />
                      {item}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                🎯 کلاس‌های فوق‌برنامه
              </h4>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <button
                  type="button"
                  className="add-session-btn"
                  onClick={() => setPlanDetails(prev => ({
                    ...prev,
                    extraClasses: [...prev.extraClasses, { day: 'شنبه', time: '16:00', duration: 60, name: '' }]
                  }))}
                >
                  <FiPlus /> افزودن کلاس
                </button>
              </div>
              {planDetails.extraClasses.map((cls, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <select
                    value={cls.day}
                    onChange={(e) => {
                      const newClasses = [...planDetails.extraClasses];
                      newClasses[idx].day = e.target.value;
                      setPlanDetails(prev => ({ ...prev, extraClasses: newClasses }));
                    }}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', background: 'rgba(255,255,255,0.5)' }}
                  >
                    {DAYS.map(day => <option key={day} value={day}>{day}</option>)}
                  </select>
                  <input
                    type="text"
                    readOnly
                    value={cls.time || ''}
                    placeholder="--:--"
                    onClick={() => openTimeModalForAI(cls.time, (v) => {
                      const newClasses = [...planDetails.extraClasses];
                      newClasses[idx].time = v;
                      setPlanDetails(prev => ({ ...prev, extraClasses: newClasses }));
                    })}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', width: '100px', cursor: 'pointer', direction: 'ltr', textAlign: 'center' }}
                  />
                  <select
                    value={cls.duration || 60}
                    onChange={(e) => {
                      const newClasses = [...planDetails.extraClasses];
                      newClasses[idx].duration = parseInt(e.target.value);
                      setPlanDetails(prev => ({ ...prev, extraClasses: newClasses }));
                    }}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', background: 'rgba(255,255,255,0.5)' }}
                    title="مدت کلاس"
                  >
                    <option value="30">۳۰ دقیقه</option>
                    <option value="45">۴۵ دقیقه</option>
                    <option value="60">۱ ساعت</option>
                    <option value="90">۱:۳۰ ساعت</option>
                    <option value="120">۲ ساعت</option>
                  </select>
                  <input
                    type="text"
                    placeholder="نام کلاس"
                    value={cls.name}
                    onChange={(e) => {
                      const newClasses = [...planDetails.extraClasses];
                      newClasses[idx].name = e.target.value;
                      setPlanDetails(prev => ({ ...prev, extraClasses: newClasses }));
                    }}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', flex: 1, minWidth: '80px' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newClasses = planDetails.extraClasses.filter((_, i) => i !== idx);
                      setPlanDetails(prev => ({ ...prev, extraClasses: newClasses }));
                    }}
                    style={{
                      background: 'rgba(244,63,94,0.06)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '4px 10px',
                      color: '#f43f5e',
                      cursor: 'pointer'
                    }}
                  >
                    <FiX />
                  </button>
                </div>
              ))}
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                🎯 فعالیت‌های خارج از برنامه
              </h4>
              {/*<p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
                فعالیت‌های غیردرسی مانند ورزش، مهمانی، کلاس زبان، موسیقی و ... را ثبت کنید.
              </p>*/}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <button
                  type="button"
                  className="add-session-btn"
                  onClick={() => setPlanDetails(prev => ({
                    ...prev,
                    outsideActivities: [...prev.outsideActivities, { day: 'شنبه', time: '16:00', duration: 60, name: '' }]
                  }))}
                >
                  <FiPlus /> افزودن فعالیت
                </button>
              </div>
              {planDetails.outsideActivities?.map((activity, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <select
                    value={activity.day}
                    onChange={(e) => {
                      const newActivities = [...planDetails.outsideActivities];
                      newActivities[idx].day = e.target.value;
                      setPlanDetails(prev => ({ ...prev, outsideActivities: newActivities }));
                    }}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', background: 'rgba(255,255,255,0.5)' }}
                  >
                    {DAYS.map(day => <option key={day} value={day}>{day}</option>)}
                  </select>
                  <input
                    type="text"
                    readOnly
                    value={activity.time || ''}
                    placeholder="--:--"
                    onClick={() => openTimeModalForAI(activity.time, (v) => {
                      const newActivities = [...planDetails.outsideActivities];
                      newActivities[idx].time = v;
                      setPlanDetails(prev => ({ ...prev, outsideActivities: newActivities }));
                    })}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', width: '100px', cursor: 'pointer', direction: 'ltr', textAlign: 'center' }}
                  />
                  <select
                    value={activity.duration || 60}
                    onChange={(e) => {
                      const newActivities = [...planDetails.outsideActivities];
                      newActivities[idx].duration = parseInt(e.target.value);
                      setPlanDetails(prev => ({ ...prev, outsideActivities: newActivities }));
                    }}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', background: 'rgba(255,255,255,0.5)' }}
                    title="مدت فعالیت"
                  >
                    <option value="30">۳۰ دقیقه</option>
                    <option value="45">۴۵ دقیقه</option>
                    <option value="60">۱ ساعت</option>
                    <option value="90">۱:۳۰ ساعت</option>
                    <option value="120">۲ ساعت</option>
                  </select>
                  <input
                    type="text"
                    placeholder="مثلاً فوتبال، مهمانی، کلاس زبان، ..."
                    value={activity.name}
                    onChange={(e) => {
                      const newActivities = [...planDetails.outsideActivities];
                      newActivities[idx].name = e.target.value;
                      setPlanDetails(prev => ({ ...prev, outsideActivities: newActivities }));
                    }}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', flex: 1, minWidth: '120px' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newActivities = planDetails.outsideActivities.filter((_, i) => i !== idx);
                      setPlanDetails(prev => ({ ...prev, outsideActivities: newActivities }));
                    }}
                    style={{
                      background: 'rgba(244,63,94,0.06)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '4px 10px',
                      color: '#f43f5e',
                      cursor: 'pointer'
                    }}
                  >
                    <FiX />
                  </button>
                </div>
              ))}
            </div>

            <div className="form-section" style={{ marginTop: '16px', borderTop: '1px solid rgba(167,139,250,0.08)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#7c3aed', marginBottom: '12px' }}>
                🎯 امتحانات این هفته مدرسه
              </h4>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <button
                  type="button"
                  className="add-session-btn"
                  onClick={() => setPlanDetails(prev => ({
                    ...prev,
                    exams: [...prev.exams, { day: 'شنبه', book: '' }]
                  }))}
                >
                  <FiPlus /> افزودن امتحان
                </button>
              </div>
              {planDetails.exams?.map((exam, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <select
                    value={exam.day}
                    onChange={(e) => {
                      const newExams = [...planDetails.exams];
                      newExams[idx].day = e.target.value;
                      setPlanDetails(prev => ({ ...prev, exams: newExams }));
                    }}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', background: 'rgba(255,255,255,0.5)' }}
                  >
                    {DAYS.map(day => <option key={day} value={day}>{day}</option>)}
                  </select>
                  <select
                    value={exam.book}
                    onChange={(e) => {
                      const newExams = [...planDetails.exams];
                      newExams[idx].book = e.target.value;
                      setPlanDetails(prev => ({ ...prev, exams: newExams }));
                    }}
                    style={{ padding: '4px 8px', border: '1px solid rgba(167,139,250,0.12)', borderRadius: '8px', fontSize: '13px', flex: 1, minWidth: '120px', background: 'rgba(255,255,255,0.5)' }}
                  >
        <option value="" disabled={!aiForm.grade}>
          {aiForm.grade ? 'انتخاب کتاب...' : '⚠️ ابتدا پایه را انتخاب کنید'}
        </option>
        {aiForm.grade && getGradeConfig(aiForm.grade, aiForm.field).books.map(book => (
          <option key={book} value={book}>{book}</option>
        ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const newExams = planDetails.exams.filter((_, i) => i !== idx);
                      setPlanDetails(prev => ({ ...prev, exams: newExams }));
                    }}
                    style={{
                      background: 'rgba(244,63,94,0.06)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '4px 10px',
                      color: '#f43f5e',
                      cursor: 'pointer'
                    }}
                  >
                    <FiX />
                  </button>
                </div>
              ))}
            </div>

<div className="form-row">
  <div className="form-group half">
    <label>نقاط قوت (اختیاری)</label>
    <input
      type="text"
      placeholder={
        planDetails.selectedSubjects.length > 0
          ? `مثلاً ${planDetails.selectedSubjects.slice(0, 2).join('، ')}`
          : 'مثلاً فارسی، ریاضی'
      }
      value={aiForm.strengths}
      onChange={(e) => setAiForm(prev => ({ ...prev, strengths: e.target.value }))}
      disabled={isLoading}
    />
    <small style={{ color: '#94a3b8', fontSize: '11px', marginTop: '2px', display: 'block' }}>
      📖 اسم کتاب‌ها را وارد کنید (مثل: فارسی، ریاضی)
    </small>
  </div>
  <div className="form-group half">
    <label>نقاط ضعف (اختیاری)</label>
    <input
      type="text"
      placeholder={
        planDetails.selectedSubjects.length > 0
          ? `مثلاً ${planDetails.selectedSubjects.slice(-2).join('، ')}`
          : 'مثلاً علوم، قرآن'
      }
      value={aiForm.weaknesses}
      onChange={(e) => setAiForm(prev => ({ ...prev, weaknesses: e.target.value }))}
      disabled={isLoading}
    />
    <small style={{ color: '#94a3b8', fontSize: '11px', marginTop: '2px', display: 'block' }}>
      📖 اسم کتاب‌ها را وارد کنید (مثل: علوم، قرآن)
    </small>
  </div>
</div>

            {/* ✅✅✅ نمایش سهمیه رایگان و هزینه سکه */}
            <div
              className={`ai-quota-box ${
                aiQuota.remaining > 0
                  ? 'is-free'
                  : coins >= AI_PLAN_COST
                    ? 'is-paid'
                    : 'is-empty'
              }`}
            >
              <div className="ai-quota-glow" aria-hidden="true" />

              {/* هدر */}
              <div className="ai-quota-header">
                <div className="ai-quota-title">
                  <span className="ai-quota-gem">💎</span>
                  <span>هزینه‌ی ساخت برنامه با AI</span>
                </div>
              </div>

              {/* دو کارت: ۳ رایگان | ۲۰ سکه */}
              <div className="ai-quota-cards">
                {/* کارت رایگان */}
                <div className={`quota-card quota-card-free ${aiQuota.remaining > 0 ? 'active' : 'exhausted'}`}>
                  <div className="quota-card-icon">
                    {aiQuota.remaining > 0 ? (
                      <span className="quota-gift">🎁</span>
                    ) : (
                      <span className="quota-check">✓</span>
                    )}
                  </div>
                  <div className="quota-card-content">
                    <div className="quota-card-label">سهمیه رایگان</div>
                    <div className="quota-card-value">
                      <span className="quota-number">{aiQuota.remaining > 0 ? aiQuota.remaining : '۰'}</span>
                      <span className="quota-unit">از {aiQuota.total}</span>
                    </div>
                    <div className="quota-card-sub">
                      {aiQuota.remaining > 0
                        ? `${aiQuota.remaining} ساخت رایگان باقی مانده`
                        : 'سهمیه رایگان تمام شد'}
                    </div>
                  </div>
                  {aiQuota.remaining > 0 && (
                    <div className="quota-card-ribbon">فعال</div>
                  )}
                </div>

                {/* جداکننده */}
                <div className="quota-divider">
                  <span className="quota-divider-line" />
                  <span className="quota-divider-text">سپس</span>
                  <span className="quota-divider-line" />
                </div>

                {/* کارت سکه */}
                <div className={`quota-card quota-card-coins ${aiQuota.remaining === 0 ? 'active' : 'standby'}`}>
                  <div className="quota-card-icon">
                    <span className="coin-stack">
                      <span className="coin-3d" />
                      <span className="coin-3d" />
                      <span className="coin-3d" />
                    </span>
                  </div>
                  <div className="quota-card-content">
                    <div className="quota-card-label">هزینه‌ی هر ساخت</div>
                    <div className="quota-card-value">
                      <span className="quota-number">{AI_PLAN_COST}</span>
                      <span className="quota-unit">سکه</span>
                    </div>
                    <div className="quota-card-sub">
                      بعد از اتمام سهمیه رایگان
                    </div>
                  </div>
                  {aiQuota.remaining === 0 && coins >= AI_PLAN_COST && (
                    <div className="quota-card-ribbon purple">فعال</div>
                  )}
                </div>
              </div>

              {/* نوار پیشرفت سهمیه رایگان */}
              {aiQuota.remaining > 0 && (
                <div className="ai-quota-progress">
                  <div className="ai-quota-progress-track">
                    <div
                      className="ai-quota-progress-fill"
                      style={{ width: `${(aiQuota.remaining / aiQuota.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* کیف پول (وقتی سهمیه تمام شد) */}
              {aiQuota.remaining === 0 && (
                <div className={`ai-quota-wallet ${coins >= AI_PLAN_COST ? 'ok' : 'low'}`}>
                  <div className="wallet-row">
                    <span className="wallet-label">موجودی سکه‌ی شما</span>
                    <span className="wallet-value">
                      <span className="coin-icon" />
                      {coins} سکه
                    </span>
                  </div>
                  {coins < AI_PLAN_COST ? (
                    <div className="wallet-shortage">
                      ⚠️ برای ساخت بعدی، <strong>{AI_PLAN_COST - coins} سکه</strong> کم دارید
                    </div>
                  ) : (
                    <div className="wallet-ready">
                      ✅ آماده برای ساخت — با تایید، {AI_PLAN_COST} سکه کسر می‌شود
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              className="btn-submit"
              onClick={buildPlanWithAI}
              disabled={
                isLoading ||
                !aiForm.grade ||
                planDetails.selectedSubjects.length === 0 ||
                planDetails.studyHoursSchoolDays <= 0 ||
                planDetails.studyHoursHolidays <= 0 ||
                (aiQuota.remaining === 0 && coins < AI_PLAN_COST)
              }
            >
              {isLoading ? (
                <><span className="spinner"></span> در حال ساخت برنامه...</>
              ) : aiQuota.remaining > 0 ? (
                <><FaRobot /> ساخت رایگان ({aiQuota.remaining} باقی‌مانده)</>
              ) : coins >= AI_PLAN_COST ? (
                <><FaRobot /> پرداخت {AI_PLAN_COST} سکه و ساخت</>
              ) : (
                <><FiAlertCircle /> سکه کافی نیست</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTimerFullscreen = () => {
    if (!timerOpen) return null;
    const session = timerSession ? plans.find(p => p.id === timerSession.planId)?.days?.[timerSession.dayIndex]?.sessions?.[timerSession.sessionIndex] : null;
    const totalDuration = session ? (session.duration || calculateDurationFromTime(session.time)) : 45;
    const progress = totalDuration > 0 ? ((timerSeconds / (totalDuration * 60)) * 100) : 100;

    return (
      <div className="timer-fullscreen">
        <div className="timer-fullscreen-content">
          <button className="timer-fullscreen-close" onClick={closeTimer}>
            <FiX />
          </button>
          <h2>{session?.book || 'مطالعه'}</h2>
          <p className="timer-fullscreen-sub">{session?.topic || ''}</p>
          <p className="timer-fullscreen-duration">⏱ مدت زمان: {formatDuration(totalDuration)}</p>
          <div className="timer-fullscreen-display">
            {formatTimerDisplay(timerSeconds)}
          </div>
          <div className="timer-fullscreen-progress">
            <div className="timer-fullscreen-progress-bar" style={{ width: `${Math.min(100, progress)}%` }} />
          </div>
          <div className="timer-fullscreen-controls">
            {!timerRunning && !timerPaused && (
              <button className="btn-start" onClick={startTimer}><FiPlay /> شروع</button>
            )}
            {timerRunning && !timerPaused && (
              <button className="btn-pause" onClick={pauseTimer}><FiPause /> مکث</button>
            )}
            {timerPaused && (
              <button className="btn-resume" onClick={resumeTimer}><FiPlay /> ادامه</button>
            )}
            <button className="btn-reset" onClick={resetTimer}><FiRefreshCw /> ریست</button>
          </div>
          <button className="timer-fullscreen-close-btn" onClick={closeTimer}>بستن</button>
        </div>
      </div>
    );
  };

  const renderParticles = () => (
    <div className="bg-particles">
      {[...Array(15)].map((_, i) => (
        <span key={i} className="bg-particle" style={{ '--i': i, '--size': (5 + (i % 8)) + 'px', '--x': (i * 7) % 100 + '%', '--y': (i * 11) % 100 + '%' }} />
      ))}
    </div>
  );

  const renderHeaderRings = () => (
    <>
      <div className="header-glow-ring ring-1"></div>
      <div className="header-glow-ring ring-2"></div>
      <div className="header-glow-ring ring-3"></div>
      <div className="header-glow-ring ring-4"></div>
    </>
  );

  const renderHeaderParticles = () => (
    <div className="header-particles">
      {[...Array(6)].map((_, i) => (
        <span key={i} className="particle" style={{ '--i': i }} />
      ))}
    </div>
  );

  return (
    <div className="lesson-planning-container" data-theme={theme}>
      {renderParticles()}
      {renderSidebar()}
      {renderAIModal()}
      {renderTimerFullscreen()}
      {renderDeleteModal()}
      {renderConflictModal()}
      {renderTimeModal()}


      <header className="lp-header">
        {renderHeaderRings()}
        {renderHeaderParticles()}
        
        {/* 🎯 دکمه بازگشت - راست‌ترین نقطه هدر */}
<button 
  className="back-home-btn" 
  onClick={() => navigate('/home')}
  title="بازگشت به خانه"
>
  <FiChevronLeft />
</button>

<div className="header-left">
  <div className="logo-wrapper">
    <div className="logo-icon"><FiBookOpen /><div className="logo-pulse"></div></div>
    <h1>برنامه‌ریز درسی <span className="role-badge">شخصی</span></h1>
  </div>
  <div className="header-subtitle"><span className="subtitle-text">🎯 هدفمند و هوشمند</span></div>
</div>


        <div className="header-right">
          <button className="new-plan-btn" onClick={() => setIsAIModalOpen(true)}><FiPlus /> جدید</button>
          {/*<button
  className="new-plan-btn"
  onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
  title="تغییر تم"
>
  {theme === 'light' ? '🌙' : '☀️'}
</button>*/}
          <div className="view-toggle">
            <button className={viewMode === 'weekly' ? 'active' : ''} onClick={() => setViewMode('weekly')}><FiGrid /></button>
            <button className={viewMode === 'daily' ? 'active' : ''} onClick={() => setViewMode('daily')}><FiList /></button>
          </div>
        </div>
      </header>

      {viewMode === 'daily' && getActivePlan && getActivePlan.days.some(d => d.sessions.length > 0) && (
        <div className="day-selector">
          {getActivePlan.days.map((day, idx) => (
            <button
              key={idx}
              className={`day-selector-btn ${selectedDay === idx ? 'active' : ''}`}
              onClick={() => setSelectedDay(idx)}
            >
              <span className="day-short">{DAYS_SHORT[idx]}</span>
              <span className="day-full">{day.day}</span>
            </button>
          ))}
        </div>
      )}

      {renderTabs()}

      <div className="lp-content">
        {renderTabContent()}
      </div>

      <AnimatePresence>
        {toast.visible && (
          <motion.div
            className={`toast ${toast.type}`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="lp-footer">
        <span>🔒 امن و رمزنگاری شده</span>
        <span>|</span>
        <span>🤖 AI: {CONFIG.modelText}</span>
        <span>|</span>
        <span>📚 {plans.length} برنامه</span>
      </footer>
    </div>
  );
}